import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import { useUserContext } from '../contexts/UserContext';
import toast from 'react-hot-toast';
import BookingDaySheet from '../components/BookingDaySheet';
import EditGymPurchaseModal from '../components/EditGymPurchaseModal';
import type { GymPurchase } from '../components/ViewGymPurchaseModal';
import type { QuickBookPrefill } from '../components/BookingDaySheet';
import { useUserPreference } from '../hooks/useUserPreference';
import '../components/calendar/adminCalendar.css';
import { CAL_TYPE_ORDER, HEAT_RAMP } from '../components/calendar/calendarTypes';
import type { BookableType as CalBookableType, CalSession } from '../components/calendar/calendarTypes';
import { hourWindow, staffNamer, toCalSession, tzNow } from '../components/calendar/calendarAdapter';
import type { ApiSession } from '../components/calendar/calendarAdapter';
import { BranchSelect, BusyPill, Segmented, TypeFilter, TypeLegend, WeekNav } from '../components/calendar/CalendarControls';
import { CalendarList, WeekPulse } from '../components/calendar/WeekPulse';
import type { HeatMetric, PulseDay } from '../components/calendar/WeekPulse';
import { FullTimelineModal } from '../components/calendar/FullTimelineModal';
import SeatLimitField, { seatLimitPayload } from '../components/schedule/SeatLimitField';
import type { Density, TimelineInit } from '../components/calendar/FullTimelineModal';
import {
  Plus, X, Loader2, Users, Clock, Maximize2,
  MapPin, CalendarDays, CheckCircle2, XCircle, AlertCircle, DoorOpen, Pencil,
  UserCheck, BookOpen, Search, ToggleLeft, ToggleRight, Tag, HelpCircle, Repeat2, Zap
} from 'lucide-react';

// ─── Interfaces ────────────────────────────────────────────────────────────────

type BookableType = 'class' | 'spa' | 'pt' | 'consult' | 'facility';
type ScheduleMode = 'timetable' | 'appointments' | 'grid';

// Availability-window day picker (0=Sun … 6=Sat), Monday-first for display.
const DAY_OPTIONS = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

interface GymClass {
  id: string;
  gymId?: string;
  name: string;
  bookableType?: BookableType;
  description?: string | null;
  category: string | null;
  defaultDurationMinutes: number;
  defaultCapacity: number;
  level?: string | null;
  tags?: string[];
  coverImageUrl?: string | null;
  bookingOpenHours?: number | null;
  cancellationCutoffHours?: number;
  isActive?: boolean;
  createdAt?: string;
  // Booking V2
  scheduleMode?: ScheduleMode;
  onlineBookable?: boolean;
  staffRequired?: boolean;
  assignmentPolicy?: 'member' | 'auto' | 'manual';
  limitToConfiguredHours?: boolean;
  bufferBeforeMin?: number;
  bufferAfterMin?: number;
  /** Null = follows the gym's member booking cut-off. */
  minNoticeMinutes?: number | null;
  maxAdvanceDays?: number;
  minDurationCells?: number | null;
  maxDurationCells?: number | null;
  maxSeatsPerBooking?: number | null;
  staff?: Array<{ userId: string; user?: { id: string; name: string | null; displayName: string | null } }>;
  availability?: Array<{ id?: string; staffId: string | null; dayOfWeek: number; startTime: string; endTime: string }>;
  _count?: { sessions: number };
}

interface Trainer {
  id: string;
  name: string | null;
  displayName: string | null;
  role?: string;
  /** Every branch the provider works at (from /trainer-availability/trainers) */
  branchIds?: string[];
}

interface Branch {
  id: string;
  name: string;
  displayName: string;
  gymId?: string;
}

interface Room {
  id: string;
  name: string;
  kind?: string;
  branchId: string;
  branch: { id: string; name: string; displayName: string };
}

interface Service {
  id: string;
  name: string;
  defaultPrice: number;
  serviceType: string;
}

interface ClassSession {
  id: string;
  classId: string;
  branchId: string;
  gymId: string;
  trainerId: string | null;
  roomId: string | null;
  recurringTemplateId: string | null;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  bookedCount: number;
  waitlistCount: number;
  status: string;
  /** Booking V2: how this session came to exist — scheduled | appointment | grid */
  sourceMode?: string;
  onlineLink: string | null;
  notes: string | null;
  class: { id: string; name: string; bookableType?: BookableType; category: string | null; level: string | null };
  branch: { id: string; name: string; displayName: string };
  room: { id: string; name: string } | null;
  service: { id: string; name: string; defaultPrice: number } | null;
  _count: { bookings: number; waitlist: number };
}

interface BranchRoom {
  id: string;
  name: string;
  kind?: string;
  branchId: string;
  gymId: string;
  capacity: number | null;
  isActive: boolean;
  branch: { id: string; name: string; displayName: string };
}

interface RecurringTemplate {
  id: string;
  gymId: string;
  classId: string;
  branchId: string;
  trainerId: string | null;
  roomId: string | null;
  serviceId: string | null;
  dayOfWeek: number | null;
  startTime: string | null;
  endTime: string | null;
  capacity: number;
  isActive: boolean;
  generateWeeksAhead: number;
  class: { id: string; name: string; category: string | null };
  branch: { id: string; name: string; displayName: string };
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: <CalendarDays className="w-3 h-3" /> },
  in_progress: { label: 'In Progress', color: 'bg-green-50 text-green-700 border-green-200', icon: <CheckCircle2 className="w-3 h-3" /> },
  completed: { label: 'Completed', color: 'bg-gray-50 text-gray-600 border-gray-200', icon: <CheckCircle2 className="w-3 h-3" /> },
  cancelled: { label: 'Cancelled', color: 'bg-red-50 text-red-600 border-red-200', icon: <XCircle className="w-3 h-3" /> },
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'All Levels'];

// ─── Bookable types ────────────────────────────────────────────────────────────
// Mirrors the server's BOOKABLE_TYPE_DEFAULTS — picking a type presets the form,
// but every value stays editable.

interface BookableTypeMeta {
  key: BookableType;
  label: string;
  desc: string;
  example: string;
  defaults: { capacity: number; duration: number; cutoff: number };
  /** Field visibility / labelling in forms */
  showLevel: boolean;
  showTags: boolean;
  capacityLocked: boolean;
  trainerLabel: string;
  trainerRequired: boolean;
  roomLabel: string;
  roomRequired: boolean;
  showTrainer: boolean;
  /** Preferred BranchRoom.kind for the room picker */
  roomKind: string | null;
  categoryHints: string[];
}

const BOOKABLE_TYPE_META: BookableTypeMeta[] = [
  {
    key: 'class', label: 'Class', desc: 'Group session, many members', example: 'e.g. Morning Yoga, HIIT',
    defaults: { capacity: 20, duration: 60, cutoff: 2 },
    showLevel: true, showTags: true, capacityLocked: false,
    trainerLabel: 'Trainer', trainerRequired: false, roomLabel: 'Room', roomRequired: false,
    showTrainer: true, roomKind: null,
    categoryHints: ['Yoga', 'HIIT', 'Cycling', 'Strength', 'Zumba', 'Pilates'],
  },
  {
    key: 'spa', label: 'Spa', desc: '1:1 treatment or recovery', example: 'e.g. Sports Massage, Cold Plunge',
    defaults: { capacity: 1, duration: 45, cutoff: 12 },
    showLevel: false, showTags: true, capacityLocked: false,
    trainerLabel: 'Therapist', trainerRequired: false, roomLabel: 'Therapy Room', roomRequired: false,
    showTrainer: true, roomKind: 'therapy',
    categoryHints: ['Massage', 'Recovery', 'Sauna', 'Cold Plunge', 'Physio'],
  },
  {
    key: 'pt', label: 'Personal', desc: '1:1 training with a coach', example: 'e.g. PT — Strength Block',
    defaults: { capacity: 1, duration: 60, cutoff: 12 },
    showLevel: false, showTags: true, capacityLocked: true,
    trainerLabel: 'Trainer', trainerRequired: true, roomLabel: 'Room', roomRequired: false,
    showTrainer: true, roomKind: null,
    categoryHints: ['Strength', 'Fat Loss', 'Mobility', 'Rehab'],
  },
  {
    key: 'consult', label: 'Consult', desc: 'Short appointment', example: 'e.g. Nutrition Consult',
    defaults: { capacity: 1, duration: 30, cutoff: 24 },
    showLevel: false, showTags: false, capacityLocked: false,
    trainerLabel: 'Consultant', trainerRequired: false, roomLabel: 'Room', roomRequired: false,
    showTrainer: true, roomKind: null,
    categoryHints: ['Nutrition', 'Fitness Assessment', 'Onboarding'],
  },
  {
    key: 'facility', label: 'Facility', desc: 'Self-service resource, no staff', example: 'e.g. Badminton Court 1, Steam Room, Pool',
    defaults: { capacity: 1, duration: 60, cutoff: 4 },
    showLevel: false, showTags: true, capacityLocked: false,
    trainerLabel: 'Trainer', trainerRequired: false, roomLabel: 'Facility', roomRequired: true,
    showTrainer: false, roomKind: 'facility',
    categoryHints: ['Badminton', 'Squash', 'Tennis', 'Pickleball', 'Steam Room', 'Swimming Pool', 'Cricket Net'],
  },
];

const typeMeta = (t?: string): BookableTypeMeta =>
  BOOKABLE_TYPE_META.find(m => m.key === t) || BOOKABLE_TYPE_META[0];

// Booking V2: schedule mode per bookable — mirrors the server's per-type default
const SCHEDULE_MODE_META: { key: ScheduleMode; label: string; desc: string }[] = [
  { key: 'timetable', label: 'Fixed timetable', desc: 'Members book sessions you schedule ahead of time' },
  { key: 'appointments', label: 'Appointments (computed times)', desc: 'Times are computed from staff working hours — no pre-scheduling needed' },
  { key: 'grid', label: 'Facility grid', desc: 'Members pick free slots on a facility × time grid' },
];

const defaultModeForType = (t: BookableType): ScheduleMode =>
  t === 'facility' ? 'grid' : ['spa', 'pt', 'consult'].includes(t) ? 'appointments' : 'timetable';

// Preferred staff roles per bookable type (trainer-availability now returns `role`)
const PREFERRED_ROLES: Partial<Record<BookableType, string[]>> = {
  spa: ['therapist', 'trainer'],
  consult: ['consultant', 'trainer'],
};
function preferredTrainers(trainers: Trainer[], type?: BookableType): Trainer[] {
  const roles = PREFERRED_ROLES[typeMeta(type).key] || ['trainer'];
  const filtered = trainers.filter(t => !t.role || roles.includes(t.role));
  return filtered.length ? filtered : trainers;
}

// Booking V2 availability shapes (GET /api/class-bookings/availability)
interface AppointmentAvailability {
  date: string;
  timezone: string;
  durationMinutes: number;
  intervalMinutes: number;
  times: Array<{ startTime: string; staffIds: string[] }>;
  staff: Array<{ id: string; name: string; role: string }>;
  rooms: Array<{ id: string; name: string }>;
}

interface CourtGridAvailability {
  date: string;
  cellMinutes: number;
  dayStart: string;
  dayEnd: string;
  courts: Array<{ id: string; name: string; cells: Array<{ startTime: string; available: boolean }> }>;
  minDurationCells: number;
  maxDurationCells: number | null;
}

function NeedsStaffBadge({ session, className = '' }: { session: ClassSession; className?: string }) {
  if (!(session.bookedCount > 0 && !session.trainerId && typeMeta(session.class.bookableType).showTrainer)) return null;
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 ${className}`}>
      Needs staff
    </span>
  );
}

function SourceModeTag({ sourceMode, className = '' }: { sourceMode?: string; className?: string }) {
  if (sourceMode !== 'appointment' && sourceMode !== 'grid') return null;
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-gray-50 text-gray-500 border-gray-200 ${className}`}>
      {sourceMode === 'grid' ? 'Facility' : 'Appointment'}
    </span>
  );
}

// Calendar colour-coding per type (B6)
const TYPE_COLORS: Record<BookableType, { accent: string; chip: string }> = {
  class:   { accent: 'border-l-blue-400',    chip: 'bg-blue-50 text-blue-700 border-blue-200' },
  spa:     { accent: 'border-l-teal-400',    chip: 'bg-teal-50 text-teal-700 border-teal-200' },
  pt:      { accent: 'border-l-violet-400',  chip: 'bg-violet-50 text-violet-700 border-violet-200' },
  consult: { accent: 'border-l-amber-400',   chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  facility: { accent: 'border-l-emerald-400', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

function TypeChip({ type, className = '' }: { type?: BookableType; className?: string }) {
  const meta = typeMeta(type);
  const colors = TYPE_COLORS[meta.key];
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${colors.chip} ${className}`}>
      {meta.label}
    </span>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function getWeekDates(anchor: Date): Date[] {
  const day = anchor.getDay();
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() - ((day + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function toDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Sentinel branch value meaning "every branch of the gym". */
const ALL_BRANCHES = '__all__';

/**
 * Providers assignable at a branch. Staff carry every branch they are mapped
 * to; a branchless list (older payloads) stays visible rather than vanishing.
 */
function trainersForBranch(trainers: Trainer[], branchId?: string): Trainer[] {
  if (!branchId || branchId === ALL_BRANCHES) return trainers;
  return trainers.filter(t => !t.branchIds?.length || t.branchIds.includes(branchId));
}

// ─── SlidePanel ────────────────────────────────────────────────────────────────

function SlidePanel({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: React.ReactNode }) {
  return (
    <>
      {open && <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />}
      <div className={`fixed top-0 right-0 h-full w-full max-w-[520px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </>
  );
}

// ─── SessionCard ───────────────────────────────────────────────────────────────


// ─── CreateSessionForm (inside SlidePanel) ─────────────────────────────────────

function CreateSessionForm({
  gymId,
  preDate,
  preTime,
  classes,
  trainers,
  branches,
  rooms,
  services,
  onClose,
  onSaved,
}: {
  gymId: string;
  preDate?: string;
  preTime?: string;
  classes: GymClass[];
  trainers: Trainer[];
  branches: Branch[];
  rooms: Room[];
  services: Service[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const addMinutes = (hhmm: string, mins: number) => {
    const [h, m] = hhmm.split(':').map(Number);
    const t = Math.min(23 * 60 + 59, h * 60 + m + mins);
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
  };
  const firstClass = classes[0];
  const initialStart = preTime || '07:00';
  const [form, setForm] = useState({
    classId: firstClass?.id || '',
    branchId: branches[0]?.id || '',
    trainerId: '',
    roomId: '',
    serviceId: '',
    date: preDate || toDateStr(new Date()),
    startTime: initialStart,
    endTime: addMinutes(initialStart, firstClass?.defaultDurationMinutes || 60),
    capacity: '',
    onlineLink: '',
    notes: '',
  });

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const selectedClass = classes.find(c => c.id === form.classId);
  const meta = typeMeta(selectedClass?.bookableType);
  const allBranches = form.branchId === ALL_BRANCHES;
  // "All branches" schedules the same session in every branch — nothing that is
  // tied to one branch (room, provider) can be pinned in that mode.
  const branchRooms = allBranches ? [] : rooms.filter(r => r.branchId === form.branchId);
  const branchTrainers = trainersForBranch(trainers, form.branchId);
  // Prefer rooms whose kind matches the bookable type (facilities for facility
  // bookings, therapy rooms for spa); fall back to all rooms in the branch
  const kindRooms = meta.roomKind ? branchRooms.filter(r => r.kind === meta.roomKind) : branchRooms;
  const filteredRooms = kindRooms.length > 0 ? kindRooms : branchRooms;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.classId || !form.branchId || !form.date || !form.startTime || !form.endTime) {
      return toast.error('Bookable, branch, date, and times are required');
    }
    if (!allBranches && meta.trainerRequired && !form.trainerId) {
      return toast.error(`${meta.label} sessions need a ${meta.trainerLabel.toLowerCase()} assigned`);
    }
    if (!allBranches && meta.roomRequired && !form.roomId) {
      return toast.error(`${meta.label} sessions need a ${meta.roomLabel.toLowerCase()} assigned`);
    }
    const targetBranches = allBranches ? branches : branches.filter(b => b.id === form.branchId);
    if (!targetBranches.length) return toast.error('No branch to schedule in');
    if (allBranches && (meta.trainerRequired || meta.roomRequired)) {
      return toast.error(`${meta.label} sessions need a ${meta.roomRequired ? meta.roomLabel.toLowerCase() : meta.trainerLabel.toLowerCase()}, so pick one branch`);
    }

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        'x-client-type': 'web',
      };

      const results = await Promise.all(targetBranches.map(async branch => {
        const body = {
          gymId: branch.gymId || gymId,
          branchId: branch.id,
          classId: form.classId,
          trainerId: allBranches ? null : (form.trainerId || null),
          roomId: allBranches ? null : (form.roomId || null),
          serviceId: form.serviceId || null,
          date: form.date,
          startTime: form.startTime,
          endTime: form.endTime,
          capacity: form.capacity ? parseInt(form.capacity) : selectedClass?.defaultCapacity || 20,
          onlineLink: form.onlineLink.trim() || null,
          notes: form.notes.trim() || null,
        };
        const res = await fetch(`${API_BASE_URL}/api/class-sessions`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        const data = await res.json();
        return { branch, ok: res.ok, error: data.error as string | undefined };
      }));

      const failed = results.filter(r => !r.ok);
      if (failed.length === results.length) {
        throw new Error(failed[0].error || 'Failed to create session');
      }
      if (failed.length) {
        toast.error(`Could not schedule in ${failed.map(f => f.branch.displayName || f.branch.name).join(', ')}: ${failed[0].error || 'failed'}`);
      }
      toast.success(
        results.length > 1
          ? `Session scheduled in ${results.length - failed.length} branches`
          : 'Session scheduled'
      );
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Bookable *</label>
        <select
          value={form.classId}
          onChange={e => {
            const next = classes.find(c => c.id === e.target.value);
            setForm(f => ({
              ...f,
              classId: e.target.value,
              endTime: next ? addMinutes(f.startTime, next.defaultDurationMinutes || 60) : f.endTime,
            }));
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          required
        >
          <option value="">Select a bookable</option>
          {classes.map(c => (
            <option key={c.id} value={c.id}>
              {typeMeta(c.bookableType).label !== 'Class' ? `[${typeMeta(c.bookableType).label}] ` : ''}{c.name}{c.category ? ` (${c.category})` : ''}
            </option>
          ))}
        </select>
        {selectedClass && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-500">
            <Clock className="w-3 h-3 shrink-0" />
            {selectedClass.bookingOpenHours != null ? (
              <span>
                Booking opens{' '}
                <span className="font-semibold text-gray-700">
                  {selectedClass.bookingOpenHours % 24 === 0
                    ? `${selectedClass.bookingOpenHours / 24} day${selectedClass.bookingOpenHours / 24 !== 1 ? 's' : ''}`
                    : `${selectedClass.bookingOpenHours} hr${selectedClass.bookingOpenHours !== 1 ? 's' : ''}`}
                </span>{' '}before session
              </span>
            ) : (
              <span className="text-gray-400">Booking always open</span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Branch *</label>
          <select
            value={form.branchId}
            onChange={e => setForm(f => ({ ...f, branchId: e.target.value, roomId: '', trainerId: '' }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            required
          >
            <option value="">Select branch</option>
            {branches.length > 1 && <option value={ALL_BRANCHES}>All branches ({branches.length})</option>}
            {branches.map(b => <option key={b.id} value={b.id}>{b.displayName || b.name}</option>)}
          </select>
          {allBranches && (
            <p className="text-[11px] text-gray-500 mt-1">
              One session per branch, same day and time — assign rooms and staff afterwards.
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {meta.roomLabel}{meta.roomRequired && ' *'}
          </label>
          <select
            value={form.roomId}
            onChange={e => set('roomId', e.target.value)}
            disabled={allBranches}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
            required={meta.roomRequired && !allBranches}
          >
            <option value="">{meta.roomRequired ? `Select a ${meta.roomLabel.toLowerCase()}` : `No specific ${meta.roomLabel.toLowerCase()}`}</option>
            {filteredRooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          {meta.roomRequired && filteredRooms.length === 0 && form.branchId && !allBranches && (
            <p className="text-[11px] text-amber-600 mt-1">
              No {meta.roomLabel.toLowerCase()}s in this branch yet — add one in the Resources tab first
            </p>
          )}
        </div>
      </div>

      {meta.showTrainer && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {meta.trainerLabel}{meta.trainerRequired && ' *'}
          </label>
          <select
            value={form.trainerId}
            onChange={e => set('trainerId', e.target.value)}
            disabled={allBranches}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
            required={meta.trainerRequired && !allBranches}
          >
            <option value="">{meta.trainerRequired ? `Select a ${meta.trainerLabel.toLowerCase()}` : `No ${meta.trainerLabel.toLowerCase()} assigned`}</option>
            {preferredTrainers(branchTrainers, selectedClass?.bookableType).map(t => (
              <option key={t.id} value={t.id}>{t.displayName || t.name || t.id}</option>
            ))}
          </select>
          {!allBranches && form.branchId && branchTrainers.length === 0 && (
            <p className="text-[11px] text-amber-600 mt-1">
              No {meta.trainerLabel.toLowerCase()}s attached to this branch — add them to the branch on the Team page.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
          <input
            type="date"
            value={form.date}
            onChange={e => set('date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
          <input
            type="time"
            value={form.startTime}
            onChange={e => {
              const nextStart = e.target.value;
              setForm(f => {
                const [sh, sm] = f.startTime.split(':').map(Number);
                const [eh, em] = f.endTime.split(':').map(Number);
                const dur = Math.max(5, (eh * 60 + em) - (sh * 60 + sm));
                return { ...f, startTime: nextStart, endTime: nextStart ? addMinutes(nextStart, dur) : f.endTime };
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End *</label>
          <input
            type="time"
            value={form.endTime}
            onChange={e => set('endTime', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Capacity
            {selectedClass && <span className="text-gray-400 font-normal"> (default {selectedClass.defaultCapacity})</span>}
          </label>
          <input
            type="number"
            min={1}
            value={form.capacity}
            onChange={e => set('capacity', e.target.value)}
            placeholder={selectedClass?.defaultCapacity?.toString() || '20'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pricing Service</label>
          <select
            value={form.serviceId}
            onChange={e => set('serviceId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          >
            <option value="">Free / Manual</option>
            {services.map(s => (
              <option key={s.id} value={s.id}>
                {s.name} — ₹{s.defaultPrice}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Online Link (optional)</label>
        <input
          type="url"
          value={form.onlineLink}
          onChange={e => set('onlineLink', e.target.value)}
          placeholder="https://meet.google.com/…"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          rows={2}
          placeholder="Any special instructions for this session"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-none"
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Schedule
        </button>
      </div>
    </form>
  );
}

const inrAmount = (n: number) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** The roster's money tag for a booking — from `payment` on GET /api/class-bookings (routes/classBookings.ts). */
function bookingPayTag(p?: { state: string; by: string; amountDue: number; total: number } | null): { label: string; className: string; title?: string } | null {
  if (!p) return null;
  if (p.state === 'covered') {
    return {
      // 'drop_in' here is an old pay-per-visit booking with no invoice on file: nothing to collect against.
      label: p.by === 'plan' ? 'Plan' : p.by === 'pack' ? 'Pack' : p.by === 'free' ? 'Free' : 'Drop-in',
      className: 'bg-gray-50 text-gray-600 border-gray-200',
      title: p.by === 'plan' ? 'Included in their plan' : p.by === 'pack' ? 'Booked on a pack' : p.by === 'free' ? 'Nothing to pay' : 'No invoice on file for this booking',
    };
  }
  if (p.state === 'paid') return { label: 'Paid', className: 'bg-green-50 text-green-700 border-green-200', title: `${inrAmount(p.total)} paid` };
  if (p.state === 'void') return { label: 'Invoice cancelled', className: 'bg-gray-50 text-gray-500 border-gray-200' };
  return {
    label: `${p.state === 'partial' ? 'Part-paid' : 'Unpaid'} · ${inrAmount(p.amountDue)}`,
    className: 'bg-red-50 text-red-700 border-red-200',
    title: `${inrAmount(p.amountDue)} of ${inrAmount(p.total)} still to collect`,
  };
}

// ─── SessionDetailPanel (right-side slide) ─────────────────────────────────────

function SessionDetailPanel({ session, onClose, onUpdated, trainers, rooms }: {
  session: ClassSession;
  onClose: () => void;
  onUpdated: () => void;
  trainers: Trainer[];
  rooms: Room[];
}) {
  const [updating, setUpdating] = useState(false);
  const spotsLeft = session.capacity - session.bookedCount;
  const statusCfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.scheduled;

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    startTime: session.startTime,
    endTime: session.endTime,
    notes: session.notes ?? '',
    trainerId: session.trainerId ?? '',
    roomId: session.roomId ?? '',
    onlineLink: session.onlineLink ?? '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editSeriesDialog, setEditSeriesDialog] = useState<{ payload: any } | null>(null);

  const [cancelDialog, setCancelDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ followingCount: number; withBookings: number; counting: boolean } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [bookings, setBookings] = useState<any[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  // The booking the desk is cancelling, and — once done — what was paid on it, to refund by hand.
  const [cancelBookingTarget, setCancelBookingTarget] = useState<any | null>(null);
  const [cancellingBooking, setCancellingBooking] = useState(false);
  const [cancelledPaid, setCancelledPaid] = useState<Array<{ id: string; invoiceId: string | null; paidAmount: number; pendingAmount: number }> | null>(null);
  // Collecting for an unpaid drop-in: the booking's own sale, opened on its payment step.
  const [collecting, setCollecting] = useState<GymPurchase | null>(null);
  const [openingSale, setOpeningSale] = useState<string | null>(null);

  const [addingMember, setAddingMember] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingBooking, setAddingBooking] = useState(false);

  // Compute session time and check-in window
  const sessionDateTime = new Date(`${session.date.slice(0, 10)}T${session.startTime}:00`);
  const sessionEnd = new Date(`${session.date.slice(0, 10)}T${session.endTime}:00`);
  const isPast = new Date() > sessionDateTime;
  const isEnded = new Date() > sessionEnd;
  const canCheckin = new Date() >= new Date(sessionDateTime.getTime() - 15 * 60 * 1000);
  const isOneOnOne = session.class.bookableType === 'pt' || session.class.bookableType === 'spa' || session.class.bookableType === 'consult';
  const [markingNoShows, setMarkingNoShows] = useState(false);

  const markRemainingNoShow = async () => {
    setMarkingNoShows(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/class-sessions/${session.id}/mark-no-shows`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to mark no-shows');
      toast.success(`${data.data.marked} marked as no-show`);
      setBookings(prev => prev.map(b => b.status === 'booked' ? { ...b, status: 'no_show', autoMarked: false } : b));
      onUpdated();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setMarkingNoShows(false);
    }
  };

  const fetchBookings = async (quiet = false) => {
    if (!quiet) setLoadingBookings(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/class-bookings?sessionId=${session.id}`, {
        headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
      });
      const data = await res.json();
      if (data.success) setBookings(data.data);
    } catch {}
    finally { setLoadingBookings(false); }
  };

  useEffect(() => {
    fetchBookings();
  }, [session.id]);

  // Open the booking's sale on its payment step — amount, member and invoice
  // already filled in — so the desk only picks how it was paid.
  const openCollect = async (purchaseId: string) => {
    setOpeningSale(purchaseId);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/gym-purchases/${purchaseId}`, {
        headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not open the purchase');
      setCollecting(data.data);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setOpeningSale(null);
    }
  };

  useEffect(() => {
    if (!memberSearch.trim()) { setMemberResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data: { session: authSess } } = await supabase.auth.getSession();
        if (!authSess) return;
        const res = await fetch(`${API_BASE_URL}/api/members?searchTerm=${encodeURIComponent(memberSearch)}&limit=5`, {
          headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
        });
        const data = await res.json();
        if (data.success) setMemberResults(data.data);
      } catch {}
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  const handleCheckin = async (bookingId: string) => {
    setCheckingIn(bookingId);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/class-bookings/${bookingId}/checkin`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Check-in failed');
      toast.success('Member checked in');
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'attended', checkedInAt: new Date().toISOString() } : b));
      onUpdated();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setCheckingIn(null);
    }
  };

  // A booking is cancelled whole — every spot of it — and a paid one only by
  // the gym (services/commerce/bookingCancel.ts). The money stays with the sale;
  // a refund is recorded by hand from Purchases, never through the gateway.
  const confirmCancelBooking = async () => {
    if (!cancelBookingTarget) return;
    setCancellingBooking(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/class-bookings/${cancelBookingTarget.id}?scope=group`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
        body: JSON.stringify({ action: 'cancel', cancelReason: 'Cancelled by the gym' }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not cancel the booking');
      const cancelled: string[] = data.data?.cancelled ?? [cancelBookingTarget.id];
      setBookings(prev => prev.map(b => (cancelled.includes(b.id) ? { ...b, status: 'cancelled' } : b)));
      onUpdated();
      const paid = data.data?.paidSales ?? [];
      if (paid.length) {
        setCancelledPaid(paid);
      } else {
        toast.success(cancelled.length > 1 ? `${cancelled.length} spots cancelled` : 'Booking cancelled');
        setCancelBookingTarget(null);
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setCancellingBooking(false);
    }
  };
  const closeCancelBooking = () => {
    setCancelBookingTarget(null);
    setCancelledPaid(null);
  };

  // ── Edit helpers ──────────────────────────────────────────────────────────────

  const handleEditSubmit = async (applyToSeries: boolean) => {
    setEditSeriesDialog(null);
    setSavingEdit(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;

      const payload: any = {
        startTime: editForm.startTime,
        endTime: editForm.endTime,
        notes: editForm.notes.trim() || null,
        trainerId: editForm.trainerId || null,
        roomId: editForm.roomId || null,
        onlineLink: editForm.onlineLink.trim() || null,
      };

      if (applyToSeries && session.recurringTemplateId) {
        const res = await fetch(`${API_BASE_URL}/api/class-sessions/${session.id}/update-following`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        toast.success(`Updated ${data.data?.updated} session${data.data?.updated !== 1 ? 's' : ''}`);
      } else {
        const res = await fetch(`${API_BASE_URL}/api/class-sessions/${session.id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        toast.success('Session updated');
      }

      onUpdated();
      setEditOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Could not save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleEditSave = () => {
    if (session.recurringTemplateId) {
      setEditSeriesDialog({ payload: editForm });
    } else {
      handleEditSubmit(false);
    }
  };

  // ── Delete helpers ─────────────────────────────────────────────────────────────

  const handleDeleteClick = async () => {
    if (!session.recurringTemplateId) {
      setDeleteDialog({ followingCount: 1, withBookings: 0, counting: false });
      return;
    }
    // Open straight away — every recurring session offers the series option,
    // the count from the server only fills in the wording.
    setDeleteDialog({ followingCount: 0, withBookings: 0, counting: true });
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return setDeleteDialog({ followingCount: 0, withBookings: 0, counting: false });
      const res = await fetch(`${API_BASE_URL}/api/class-sessions/${session.id}/following-count`, {
        headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
      });
      const data = await res.json();
      setDeleteDialog({ followingCount: data?.data?.following ?? 0, withBookings: data?.data?.withBookings ?? 0, counting: false });
    } catch {
      setDeleteDialog({ followingCount: 0, withBookings: 0, counting: false });
    }
  };

  const confirmDeleteOne = async () => {
    setDeleteDialog(null);
    setDeleting(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/class-sessions/${session.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success('Session deleted');
      onUpdated();
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Could not delete session');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteFollowing = async () => {
    setDeleteDialog(null);
    setDeleting(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/class-sessions/${session.id}/delete-following`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web', 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const { deleted = 0, kept = 0 } = data.data ?? {};
      const removed = `Deleted ${deleted} session${deleted !== 1 ? 's' : ''}`;
      // Sessions someone is booked into (or attended) stay: cancelling is what tells members.
      if (kept > 0) toast(`${removed}. ${kept} with bookings ${kept !== 1 ? 'were' : 'was'} kept — cancel ${kept !== 1 ? 'them' : 'it'} to tell the members.`, { duration: 7000 });
      else toast.success(removed);
      onUpdated();
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Could not delete sessions');
    } finally {
      setDeleting(false);
    }
  };

  const updateStatus = async (status: string) => {
    setCancelDialog(false);
    setUpdating(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/class-sessions/${session.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success(`Session marked as ${status}`);
      onUpdated();
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const addMemberToSession = async (memberId: string) => {
    setAddingBooking(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/class-bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authSess.access_token}`,
          'x-client-type': 'web',
        },
        body: JSON.stringify({ gymId: session.gymId, memberId, sessionId: session.id, free: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add member');
      toast.success('Member added to session');
      setAddingMember(false);
      setMemberSearch('');
      setMemberResults([]);
      const { data: { session: authSess2 } } = await supabase.auth.getSession();
      if (authSess2) {
        const bRes = await fetch(`${API_BASE_URL}/api/class-bookings?sessionId=${session.id}`, {
          headers: { Authorization: `Bearer ${authSess2.access_token}`, 'x-client-type': 'web' },
        });
        const bData = await bRes.json();
        if (bData.success) setBookings(bData.data);
      }
      onUpdated();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setAddingBooking(false);
    }
  };

  const bookingStatusColor = (status: string) => {
    switch (status) {
      case 'booked': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'attended': return 'bg-green-50 text-green-700 border-green-200';
      case 'no_show': return 'bg-gray-50 text-gray-500 border-gray-200';
      case 'cancelled': return 'bg-red-50 text-red-600 border-red-200';
      default: return 'bg-gray-50 text-gray-500 border-gray-200';
    }
  };

  return (
    <div className="p-5 space-y-4">
      {/* Header info */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-bold text-gray-900 text-base">{session.class.name}</h3>
          <TypeChip type={session.class.bookableType} />
          {isOneOnOne && <span className="text-[10px] font-bold text-gray-400">1:1</span>}
          <SourceModeTag sourceMode={session.sourceMode} />
          <NeedsStaffBadge session={session} />
        </div>
        <p className="text-sm text-gray-500">{new Date(session.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <Clock className="w-4 h-4 text-gray-400 shrink-0" />
          <span>{session.startTime} – {session.endTime}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <MapPin className="w-4 h-4 text-gray-400" />
          {session.branch.displayName || session.branch.name}
          {session.room && ` · ${session.room.name}`}
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <Users className="w-4 h-4 text-gray-400" />
          {session.bookedCount}/{session.capacity} booked
          {spotsLeft > 0 ? ` · ${spotsLeft} left` : ' · Full'}
        </div>
        {session.waitlistCount > 0 && (
          <div className="flex items-center gap-2 text-orange-600">
            <AlertCircle className="w-4 h-4" />
            {session.waitlistCount} on waitlist
          </div>
        )}
      </div>

      {/* Status badge — only for non-scheduled states */}
      {session.status !== 'scheduled' && (
        <div className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border w-fit ${statusCfg.color}`}>
          {statusCfg.icon}
          {statusCfg.label}
        </div>
      )}

      {session.notes && (
        <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">{session.notes}</p>
      )}

      {session.service && (
        <p className="text-sm text-gray-600">
          Price: <span className="font-semibold">₹{session.service.defaultPrice}</span> via {session.service.name}
        </p>
      )}

      {/* Attendees */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-black uppercase tracking-widest text-gray-400">{isOneOnOne ? 'Client' : 'Attendees'}</p>
          {isEnded && session.status !== 'cancelled' && bookings.some((b: any) => b.status === 'booked') && (
            <button
              onClick={markRemainingNoShow}
              disabled={markingNoShows}
              className="flex items-center gap-1 text-[11px] font-bold text-orange-600 bg-orange-50 border border-orange-200 px-2.5 py-1 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50"
            >
              {markingNoShows && <Loader2 className="w-3 h-3 animate-spin" />}
              Mark all remaining as no-show
            </button>
          )}
        </div>
        {loadingBookings ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : bookings.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">No bookings yet.</p>
        ) : (
          <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
            {bookings.map((b: any) => {
              // Health notes surfaced for the trainer/therapist (injuries + conditions)
              const health = b.healthNotes
                ? [b.healthNotes.injuries, ...(b.healthNotes.medicalConditions || [])].filter(Boolean).join(', ')
                : null;
              const pay = b.status === 'cancelled' ? null : bookingPayTag(b.payment);
              const owes = b.status !== 'cancelled' && b.payment?.amountDue > 0 && b.payment?.purchaseId;
              return (
              <div key={b.id} className="text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-700 truncate flex-1">{b.member?.displayName || b.member?.fullName || b.member?.name || b.memberId}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* Paid or not, before anyone is let in. */}
                    {pay && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${pay.className}`} title={pay.title}>
                        {pay.label}
                      </span>
                    )}
                    {owes && (
                      <button
                        onClick={() => openCollect(b.payment.purchaseId)}
                        disabled={openingSale === b.payment.purchaseId}
                        className="text-[11px] font-bold text-white bg-zinc-900 hover:bg-zinc-700 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap flex items-center gap-1"
                        title="Record the payment for this booking"
                      >
                        {openingSale === b.payment.purchaseId && <Loader2 className="w-3 h-3 animate-spin" />}
                        Collect
                      </button>
                    )}
                    <span
                      className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${bookingStatusColor(b.status)}`}
                      title={b.status === 'no_show' && b.autoMarked ? 'Marked automatically — click check-in to correct' : undefined}
                    >
                      {b.status}{b.status === 'no_show' && b.autoMarked ? ' · auto' : ''}
                    </span>
                    <button
                      onClick={() => handleCheckin(b.id)}
                      disabled={!canCheckin || b.status === 'attended' || b.status === 'cancelled' || checkingIn === b.id}
                      className={`p-1.5 rounded-lg transition-colors ${b.status === 'attended' ? 'text-green-600 bg-green-50' : canCheckin && b.status !== 'cancelled' ? 'text-gray-500 hover:text-green-600 hover:bg-green-50' : 'text-gray-300 cursor-not-allowed'}`}
                      title={b.status === 'attended' ? 'Checked in' : canCheckin ? 'Check in' : 'Check-in opens 15 min before class'}
                    >
                      {checkingIn === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                    </button>
                    {b.status === 'booked' && (
                      <button
                        onClick={() => setCancelBookingTarget(b)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title={b.spots > 1 ? `Cancel this booking (all ${b.spots} spots)` : 'Cancel this booking'}
                        aria-label="Cancel booking"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                {health && (
                  <p className="text-[11px] text-amber-600 truncate mt-0.5" title={health}>
                    ⚠ {health}
                  </p>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add member */}
      {!isPast && session.status === 'scheduled' && (
        <div>
          {!addingMember ? (
            <button
              onClick={() => setAddingMember(true)}
              className="text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              + Add Member
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search member..."
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                  autoFocus
                />
                {searchLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400 shrink-0" />}
                <button onClick={() => { setAddingMember(false); setMemberSearch(''); setMemberResults([]); }} className="text-gray-400 hover:text-gray-600 shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {memberResults.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                  {memberResults.map((m: any) => (
                    <button
                      key={m.id}
                      onClick={() => addMemberToSession(m.id)}
                      disabled={addingBooking}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 disabled:opacity-50 flex items-center justify-between"
                    >
                      <span className="font-medium text-gray-800">{m.name || m.displayName}</span>
                      {m.email && <span className="text-xs text-gray-400">{m.email}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      {session.status === 'scheduled' && (
        <div className="flex gap-2 pt-2 border-t border-gray-100 flex-wrap">
          <button
            onClick={() => { setEditForm({ startTime: session.startTime, endTime: session.endTime, notes: session.notes ?? '', trainerId: session.trainerId ?? '', roomId: session.roomId ?? '', onlineLink: session.onlineLink ?? '' }); setEditOpen(true); }}
            disabled={updating || deleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </button>
          <button
            onClick={() => updateStatus('completed')}
            disabled={updating || deleting}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            Mark Completed
          </button>
          <button
            onClick={() => setCancelDialog(true)}
            disabled={updating || deleting}
            className="px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteClick}
            disabled={updating || deleting}
            className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Delete
          </button>
        </div>
      )}

      {/* ── Collect for an unpaid drop-in: the sale's own payment step ── */}
      <EditGymPurchaseModal
        isOpen={!!collecting}
        purchase={collecting}
        initialTab="payment"
        onClose={() => setCollecting(null)}
        onSuccess={() => { fetchBookings(true); onUpdated(); }}
      />

      {/* ── Edit modal ── */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">Edit session</h3>
              <button onClick={() => setEditOpen(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Start time</label>
                <input
                  type="time"
                  value={editForm.startTime}
                  onChange={e => setEditForm(f => ({ ...f, startTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">End time</label>
                <input
                  type="time"
                  value={editForm.endTime}
                  onChange={e => setEditForm(f => ({ ...f, endTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Trainer</label>
                <select
                  value={editForm.trainerId}
                  onChange={e => setEditForm(f => ({ ...f, trainerId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
                >
                  <option value="">No trainer</option>
                  {preferredTrainers(trainersForBranch(trainers, session.branchId), session.class.bookableType).map(t => (
                    <option key={t.id} value={t.id}>{t.displayName || t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Room</label>
                <select
                  value={editForm.roomId}
                  onChange={e => setEditForm(f => ({ ...f, roomId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
                >
                  <option value="">No room</option>
                  {rooms.filter(r => r.branchId === session.branchId).map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Online link</label>
                <input
                  type="url"
                  value={editForm.onlineLink}
                  onChange={e => setEditForm(f => ({ ...f, onlineLink: e.target.value }))}
                  placeholder="https://…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes / Description</label>
                <textarea
                  value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  placeholder="Add session notes…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setEditOpen(false)} className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={savingEdit}
                className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-zinc-900 rounded-xl hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {savingEdit && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit series dialog ── */}
      {editSeriesDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="text-base font-bold text-gray-900">Apply changes to…</h3>
            <p className="text-sm text-gray-600">This session is part of a recurring series. Should these changes apply to future sessions as well?</p>
            <div className="flex flex-col gap-2 pt-1">
              <button
                onClick={() => handleEditSubmit(true)}
                disabled={savingEdit}
                className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-zinc-900 rounded-xl hover:bg-zinc-700 transition-colors text-left disabled:opacity-50"
              >
                This session and all future sessions in the series
              </button>
              <button
                onClick={() => handleEditSubmit(false)}
                disabled={savingEdit}
                className="w-full px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-xl hover:bg-gray-200 transition-colors text-left disabled:opacity-50"
              >
                Just this session
              </button>
              <button onClick={() => setEditSeriesDialog(null)} className="w-full px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
                Never mind
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel confirmation ── */}
      {cancelBookingTarget && (() => {
        const b = cancelBookingTarget;
        const name = b.member?.displayName || b.member?.fullName || 'this member';
        const spots: number = b.spots ?? 1;
        const pay = b.payment;
        const paidSoFar = pay ? Number(pay.total ?? 0) - Number(pay.amountDue ?? 0) : 0;
        const moneyLine = !pay
          ? null
          : pay.state === 'covered'
            ? pay.by === 'pack' ? 'The session goes back to their pack.' : pay.by === 'plan' ? 'The session goes back to their plan.' : null
            : (pay.state === 'paid' || pay.state === 'partial') && paidSoFar > 0
              ? `${inrAmount(paidSoFar)} has been paid. It stays with the sale — if a refund is due, record it in Purchases. Nothing is refunded through the payment gateway.`
              : 'Its unpaid invoice is cancelled with it.';
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
              {cancelledPaid ? (
                <>
                  <h3 className="text-base font-bold text-gray-900">Booking cancelled</h3>
                  <p className="text-sm text-gray-600">
                    The money paid stays with the sale. If a refund is due, cancel the purchase in Purchases and record the refund there.
                  </p>
                  <ul className="text-sm text-gray-800 space-y-1">
                    {cancelledPaid.map(s => (
                      <li key={s.id} className="flex justify-between gap-3">
                        <span className="truncate">{s.invoiceId || 'Sale'}</span>
                        <span className="tabular-nums whitespace-nowrap">
                          {inrAmount(s.paidAmount)} paid{s.pendingAmount > 0 ? ` · ${inrAmount(s.pendingAmount)} still open` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2 pt-1">
                    <button onClick={closeCancelBooking} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                      Done
                    </button>
                    <Link
                      to={`/purchases?search=${encodeURIComponent(cancelledPaid[0].invoiceId || '')}`}
                      className="flex-1 px-4 py-2.5 text-sm font-semibold text-center text-white bg-zinc-900 rounded-xl hover:bg-zinc-700 transition-colors"
                    >
                      Open Purchases
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-base font-bold text-gray-900">
                    {spots > 1 ? `Cancel all ${spots} spots of ${name}'s booking?` : `Cancel ${name}'s booking?`}
                  </h3>
                  <div className="text-sm text-gray-600 space-y-2">
                    {spots > 1 && <p>A booking is cancelled whole: its {spots} spots (on other sessions or courts too) go together.</p>}
                    {moneyLine && <p>{moneyLine}</p>}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={closeCancelBooking} disabled={cancellingBooking} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                      Keep it
                    </button>
                    <button
                      onClick={confirmCancelBooking}
                      disabled={cancellingBooking}
                      className="flex-1 px-4 py-2.5 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      {cancellingBooking && <Loader2 className="w-4 h-4 animate-spin" />}
                      {spots > 1 ? `Cancel all ${spots}` : 'Cancel booking'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {cancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="text-base font-bold text-gray-900">Cancel this session?</h3>
            <p className="text-sm text-gray-600">The session will be marked as cancelled. All booked members will be notified.</p>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setCancelDialog(false)} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                Never mind
              </button>
              <button
                onClick={() => updateStatus('cancelled')}
                disabled={updating}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors disabled:opacity-50"
              >
                Yes, cancel it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete dialog ── */}
      {deleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="text-base font-bold text-gray-900">Delete session</h3>
            {session.recurringTemplateId ? (
              <p className="text-sm text-gray-600">
                This session is part of a recurring series.{' '}
                {deleteDialog.counting ? (
                  <span className="text-gray-400">Counting the rest of the series…</span>
                ) : deleteDialog.followingCount > 1 ? (
                  <>
                    There are{' '}
                    <span className="font-semibold text-gray-900">{deleteDialog.followingCount} sessions</span>{' '}
                    from this date onwards. What would you like to delete?
                  </>
                ) : (
                  <>It is the last session of the series from this date onwards.</>
                )}
                {!deleteDialog.counting && deleteDialog.withBookings > 0 && (
                  <span className="block mt-2 text-amber-700">
                    {deleteDialog.withBookings} of them {deleteDialog.withBookings !== 1 ? 'have' : 'has'} bookings and will be kept —
                    cancel {deleteDialog.withBookings !== 1 ? 'those' : 'it'} instead so the members are told.
                  </span>
                )}
              </p>
            ) : (
              <p className="text-sm text-gray-600">This will permanently remove the session. This cannot be undone.</p>
            )}
            <div className="flex flex-col gap-2 pt-1">
              {session.recurringTemplateId && (
                <button
                  onClick={confirmDeleteFollowing}
                  disabled={deleting || deleteDialog.counting}
                  className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors text-left disabled:opacity-50 flex items-center gap-2"
                >
                  {deleteDialog.counting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Delete this and all following sessions
                  {!deleteDialog.counting && deleteDialog.followingCount > 1 && ` (${deleteDialog.followingCount - deleteDialog.withBookings})`}
                </button>
              )}
              <button
                onClick={confirmDeleteOne}
                disabled={deleting}
                className="w-full px-4 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors text-left disabled:opacity-50"
              >
                Delete just this session
              </button>
              <button onClick={() => setDeleteDialog(null)} className="w-full px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                Never mind
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BookableForm (for slide panel) ────────────────────────────────────────────

function ClassForm({
  gymClass,
  gymId,
  onClose,
  onSaved,
}: {
  gymClass: Partial<GymClass> | null;
  gymId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!gymClass?.id;
  const [saving, setSaving] = useState(false);

  const initBookingOpen = (() => {
    const h = gymClass?.bookingOpenHours;
    if (h != null) return h % 24 === 0 ? { value: String(h / 24), unit: 'days' as const } : { value: String(h), unit: 'hours' as const };
    return isEdit ? { value: '', unit: 'days' as const } : { value: '14', unit: 'days' as const };
  })();

  const [bookableType, setBookableType] = useState<BookableType>(gymClass?.bookableType || 'class');
  const meta = typeMeta(bookableType);

  // Booking mode — keep the saved value on edit, per-type default on create
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>(
    gymClass?.scheduleMode || defaultModeForType(gymClass?.bookableType || 'class')
  );

  const [form, setForm] = useState({
    name: gymClass?.name || '',
    description: gymClass?.description || '',
    category: gymClass?.category || '',
    defaultDurationMinutes: gymClass?.defaultDurationMinutes?.toString() || '60',
    defaultCapacity: gymClass?.defaultCapacity?.toString() || '20',
    level: gymClass?.level || '',
    tags: gymClass?.tags?.join(', ') || '',
    bookingValue: initBookingOpen.value,
    bookingUnit: initBookingOpen.unit as 'days' | 'hours',
    cancellationCutoffHours: gymClass?.cancellationCutoffHours?.toString() || '2',
    // Booking V2
    onlineBookable: gymClass?.onlineBookable ?? true,
    assignmentPolicy: gymClass?.assignmentPolicy || 'auto',
    bufferBeforeMin: gymClass?.bufferBeforeMin?.toString() || '0',
    bufferAfterMin: gymClass?.bufferAfterMin?.toString() || '0',
    // Empty = follow the gym's member booking cut-off.
    minNoticeMinutes: gymClass?.minNoticeMinutes != null ? String(gymClass.minNoticeMinutes) : '',
    maxAdvanceDays: gymClass?.maxAdvanceDays?.toString() || '30',
    minDurationCells: gymClass?.minDurationCells?.toString() || '1',
    maxDurationCells: gymClass?.maxDurationCells?.toString() || '',
    // Empty = the mode's default (3 slots in a row, 4 courts).
    maxSeatsPerBooking: gymClass?.maxSeatsPerBooking != null ? String(gymClass.maxSeatsPerBooking) : '',
    limitToConfiguredHours: gymClass?.limitToConfiguredHours ?? false,
    staffRequired: gymClass?.staffRequired ?? ((gymClass?.bookableType || 'class') !== 'facility'),
  });

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  // ── Staff assignment + availability (Booking V2) ──
  const [staffIds, setStaffIds] = useState<string[]>(gymClass?.staff?.map(s => s.userId) ?? []);
  const [availWindows, setAvailWindows] = useState<{ dayOfWeek: number; startTime: string; endTime: string }[]>(
    (gymClass?.availability ?? []).filter(a => !a.staffId).map(a => ({ dayOfWeek: a.dayOfWeek, startTime: a.startTime, endTime: a.endTime }))
  );
  const [providers, setProviders] = useState<{ id: string; name: string | null; displayName: string | null; role?: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      try {
        const res = await fetch(`${API_BASE_URL}/api/trainer-availability/trainers?gymId=${gymId}`, {
          headers: { Authorization: `Bearer ${session.access_token}`, 'x-client-type': 'web' },
        });
        const json = await res.json();
        if (!cancelled && res.ok && Array.isArray(json.data)) setProviders(json.data);
      } catch { /* non-fatal — picker just stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [gymId]);

  const toggleStaff = (id: string) => setStaffIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const addWindow = () => setAvailWindows(prev => [...prev, { dayOfWeek: 1, startTime: '09:00', endTime: '17:00' }]);
  const updateWindow = (i: number, patch: Partial<{ dayOfWeek: number; startTime: string; endTime: string }>) =>
    setAvailWindows(prev => prev.map((w, idx) => (idx === i ? { ...w, ...patch } : w)));
  const removeWindow = (i: number) => setAvailWindows(prev => prev.filter((_, idx) => idx !== i));

  // Picking a type presets capacity / duration / cancellation window (create
  // only — editing keeps whatever the admin already saved). Everything stays
  // overridable.
  const pickType = (t: BookableType) => {
    setBookableType(t);
    if (!isEdit) {
      const d = typeMeta(t).defaults;
      setScheduleMode(defaultModeForType(t));
      setForm(f => ({
        ...f,
        defaultCapacity: String(d.capacity),
        defaultDurationMinutes: String(d.duration),
        cancellationCutoffHours: String(d.cutoff),
        staffRequired: t !== 'facility',
        ...(typeMeta(t).showLevel ? {} : { level: '' }),
      }));
    }
  };

  const effectiveCapacity = meta.capacityLocked ? '1' : form.defaultCapacity;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const body: any = {
        name: form.name.trim(),
        bookableType,
        description: form.description.trim() || null,
        category: form.category.trim() || null,
        defaultDurationMinutes: parseInt(form.defaultDurationMinutes) || meta.defaults.duration,
        defaultCapacity: parseInt(effectiveCapacity) || meta.defaults.capacity,
        level: meta.showLevel ? form.level || null : null,
        tags: meta.showTags && form.tags ? form.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [],
        bookingOpenHours: form.bookingValue ? parseInt(form.bookingValue) * (form.bookingUnit === 'days' ? 24 : 1) : null,
        cancellationCutoffHours: parseInt(form.cancellationCutoffHours) || meta.defaults.cutoff,
        // Booking V2
        scheduleMode,
        onlineBookable: form.onlineBookable,
        assignmentPolicy: form.assignmentPolicy,
        bufferBeforeMin: parseInt(form.bufferBeforeMin) || 0,
        bufferAfterMin: parseInt(form.bufferAfterMin) || 0,
        minNoticeMinutes: form.minNoticeMinutes.trim() === '' ? null : Math.max(0, parseInt(form.minNoticeMinutes) || 0),
        maxAdvanceDays: parseInt(form.maxAdvanceDays) || 30,
        minDurationCells: form.minDurationCells ? parseInt(form.minDurationCells) : null,
        maxDurationCells: form.maxDurationCells ? parseInt(form.maxDurationCells) : null,
        ...seatLimitPayload(scheduleMode, form.maxSeatsPerBooking),
        limitToConfiguredHours: form.limitToConfiguredHours,
        staffRequired: form.staffRequired,
        // Self-service bookables (facilities) have no providers.
        staffIds: form.staffRequired ? staffIds : [],
      };

      // Availability windows only shape computed appointment slots.
      if (scheduleMode === 'appointments') {
        body.availability = availWindows.map(w => ({ ...w, staffId: null }));
      }

      if (!isEdit) body.gymId = gymId;

      const url = isEdit
        ? `${API_BASE_URL}/api/classes/${gymClass!.id}`
        : `${API_BASE_URL}/api/classes`;
      const method = isEdit ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          'x-client-type': 'web',
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save bookable');
      if (Array.isArray(data.warnings)) {
        data.warnings.forEach((w: string) => toast(w, { icon: '⚠️' }));
      }

      toast.success(isEdit ? 'Bookable updated' : 'Bookable created');
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      {/* Type picker — the FIRST decision */}
      <div data-tour="tour-type-picker">
        <label className="block text-sm font-medium text-gray-700 mb-2">What are members booking? *</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {BOOKABLE_TYPE_META.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => pickType(t.key)}
              className={`text-left px-3.5 py-2.5 rounded-xl border-2 transition-all ${
                bookableType === t.key
                  ? 'border-zinc-900 bg-zinc-50 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-900">{t.label}</span>
                <TypeChip type={t.key} />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
              <p className="text-[11px] text-gray-400">{t.example}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={e => set('name', e.target.value)}
          placeholder={meta.example.replace('e.g. ', 'e.g. ')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <input
            type="text"
            list={`category-hints-${bookableType}`}
            value={form.category}
            onChange={e => set('category', e.target.value)}
            placeholder={meta.categoryHints.slice(0, 3).join(', ') + '…'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
          <datalist id={`category-hints-${bookableType}`}>
            {meta.categoryHints.map(h => <option key={h} value={h} />)}
          </datalist>
        </div>
        {meta.showLevel && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
            <select
              value={form.level}
              onChange={e => set('level', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            >
              <option value="">Any level</option>
              {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
          <input
            type="number"
            min={5}
            value={form.defaultDurationMinutes}
            onChange={e => set('defaultDurationMinutes', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Default Capacity
            {meta.capacityLocked && <span className="text-gray-400 font-normal"> (1:1)</span>}
          </label>
          <input
            type="number"
            min={1}
            value={effectiveCapacity}
            onChange={e => set('defaultCapacity', e.target.value)}
            disabled={meta.capacityLocked}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
          />
          {!meta.capacityLocked && meta.defaults.capacity === 1 && parseInt(form.defaultCapacity) > 1 && (
            <p className="text-[11px] text-amber-600 mt-1">
              {meta.label} bookings are usually 1:1 — capacity {form.defaultCapacity} lets several members book the same slot
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={form.description}
          onChange={e => set('description', e.target.value)}
          rows={2}
          placeholder="What members can expect"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-none"
        />
      </div>

      {meta.showTags && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
          <input
            type="text"
            value={form.tags}
            onChange={e => set('tags', e.target.value)}
            placeholder="strength, cardio, flexibility"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
        </div>
      )}

      <div className="pt-2 border-t border-gray-100 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Booking Opens Before Session</label>
          <div className="flex gap-2">
            <input
              type="number"
              min={0}
              value={form.bookingValue}
              onChange={e => set('bookingValue', e.target.value)}
              placeholder="Always open"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            />
            <select
              value={form.bookingUnit}
              onChange={e => set('bookingUnit', e.target.value)}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            >
              <option value="days">days</option>
              <option value="hours">hours</option>
            </select>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Leave blank = booking always open</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Cutoff (hrs)</label>
          <input
            type="number"
            min={0}
            value={form.cancellationCutoffHours}
            onChange={e => set('cancellationCutoffHours', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
        </div>
      </div>

      {/* ── Booking mode (V2) ── */}
      <div className="pt-2 border-t border-gray-100 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Booking mode</label>
          <div className="space-y-2">
            {SCHEDULE_MODE_META.map(m => (
              <button
                key={m.key}
                type="button"
                onClick={() => setScheduleMode(m.key)}
                className={`w-full text-left px-3.5 py-2.5 rounded-xl border-2 transition-all ${
                  scheduleMode === m.key
                    ? 'border-zinc-900 bg-zinc-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <span className="text-sm font-bold text-gray-900">{m.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{m.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-700">Visible in member app</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Off = staff book it from here; members never see it</p>
          </div>
          <button
            type="button"
            onClick={() => set('onlineBookable', !form.onlineBookable)}
            className="shrink-0 mt-0.5"
            title={form.onlineBookable ? 'Visible in member app' : 'Hidden from member app'}
          >
            {form.onlineBookable
              ? <ToggleRight className="w-8 h-8 text-green-500" />
              : <ToggleLeft className="w-8 h-8 text-gray-300" />}
          </button>
        </div>

        {scheduleMode === 'appointments' && (
          <div className="space-y-4">
            {form.staffRequired && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff assignment</label>
              <select
                value={form.assignmentPolicy}
                onChange={e => set('assignmentPolicy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
              >
                <option value="member">Member picks the provider (names shown)</option>
                <option value="auto">Auto-assign least-busy staff (names hidden)</option>
                <option value="manual">Leave unassigned for manual assignment (names hidden)</option>
              </select>
              <p className="text-[11px] text-gray-400 mt-1">
                {form.assignmentPolicy === 'member'
                  ? 'Members choose from the assigned staff below, or "Anyone".'
                  : form.assignmentPolicy === 'auto'
                  ? 'Members book a time only; the least-busy assigned staff is picked automatically.'
                  : 'Members book a time only; you assign a provider yourself afterwards.'}
              </p>
            </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Buffer before (min)</label>
                <input
                  type="number"
                  min={0}
                  value={form.bufferBeforeMin}
                  onChange={e => set('bufferBeforeMin', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Buffer after (min)</label>
                <input
                  type="number"
                  min={0}
                  value={form.bufferAfterMin}
                  onChange={e => set('bufferAfterMin', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Booking notice (min)</label>
                <input
                  type="number"
                  min={0}
                  value={form.minNoticeMinutes}
                  onChange={e => set('minNoticeMinutes', e.target.value)}
                  placeholder="Gym default"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
                <p className="text-[11px] text-gray-400 mt-1">Members can't book closer than this. Empty follows the gym's cut-off; staff are never blocked.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max advance (days)</label>
                <input
                  type="number"
                  min={1}
                  value={form.maxAdvanceDays}
                  onChange={e => set('maxAdvanceDays', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
            </div>

            {/* Available hours — bound the computed slot grid */}
            <div className="pt-2 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1">Opening hours</label>
              <p className="text-[11px] text-gray-400 mb-2">
                When this is offered at all. Members see times inside these hours <em>and</em> inside a
                provider's working hours — set those per person in Team → Availability. Leave empty for the
                full booking day.
              </p>
              <div className="space-y-2">
                {availWindows.map((w, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select
                      value={w.dayOfWeek}
                      onChange={e => updateWindow(i, { dayOfWeek: parseInt(e.target.value) })}
                      className="px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                    >
                      {DAY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                    <input
                      type="time"
                      value={w.startTime}
                      onChange={e => updateWindow(i, { startTime: e.target.value })}
                      className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                    />
                    <span className="text-gray-400 text-sm">–</span>
                    <input
                      type="time"
                      value={w.endTime}
                      onChange={e => updateWindow(i, { endTime: e.target.value })}
                      className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                    />
                    <button type="button" onClick={() => removeWindow(i)} className="text-gray-400 hover:text-red-500 p-1" aria-label="Remove window">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addWindow}
                className="mt-2 text-sm font-medium text-zinc-700 hover:text-zinc-900 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add hours
              </button>
            </div>

            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Only offer configured hours</p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  On: a provider with no hours set offers no slots. Off: they're bookable the whole day.
                </p>
              </div>
              <button
                type="button"
                onClick={() => set('limitToConfiguredHours', !form.limitToConfiguredHours)}
                className="shrink-0 mt-0.5"
                title={form.limitToConfiguredHours ? 'Only configured hours' : 'Whole day when no hours set'}
              >
                {form.limitToConfiguredHours
                  ? <ToggleRight className="w-8 h-8 text-green-500" />
                  : <ToggleLeft className="w-8 h-8 text-gray-300" />}
              </button>
            </div>
          </div>
        )}

        {scheduleMode === 'grid' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min duration (cells)</label>
              <input
                type="number"
                min={1}
                value={form.minDurationCells}
                onChange={e => set('minDurationCells', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
              />
              {form.minDurationCells && (
                <p className="text-[11px] text-gray-400 mt-1">
                  = {(parseInt(form.minDurationCells) || 1) * (parseInt(form.defaultDurationMinutes) || meta.defaults.duration)} minutes
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max duration (cells)</label>
              <input
                type="number"
                min={1}
                value={form.maxDurationCells}
                onChange={e => set('maxDurationCells', e.target.value)}
                placeholder="No limit"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
              />
              {form.maxDurationCells && (
                <p className="text-[11px] text-gray-400 mt-1">
                  = {(parseInt(form.maxDurationCells) || 1) * (parseInt(form.defaultDurationMinutes) || meta.defaults.duration)} minutes
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max advance (days)</label>
              <input
                type="number"
                min={1}
                value={form.maxAdvanceDays}
                onChange={e => set('maxAdvanceDays', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {/* Staff — a bookable either needs a provider or is a self-service resource */}
      <div className="pt-2 border-t border-gray-100">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-700">Needs a staff member</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Off = a self-service facility (court, steam room, pool…) booked with no provider.
            </p>
          </div>
          <button
            type="button"
            onClick={() => set('staffRequired', !form.staffRequired)}
            className="shrink-0 mt-0.5"
            title={form.staffRequired ? 'Provider assigned' : 'Self-service, no provider'}
          >
            {form.staffRequired
              ? <ToggleRight className="w-8 h-8 text-green-500" />
              : <ToggleLeft className="w-8 h-8 text-gray-300" />}
          </button>
        </div>

        {form.staffRequired && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Staff for this bookable</label>
            <p className="text-[11px] text-gray-400 mb-2">
              {scheduleMode === 'appointments'
                ? 'Members can only be booked with the staff you assign here. Leave empty to allow any eligible provider.'
                : 'Who runs this. Leave empty to allow any eligible provider.'}
            </p>
            {providers.length === 0 ? (
              <p className="text-xs text-gray-400">No staff found — add trainers, therapists or consultants to your team first.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {providers.map(p => {
                  const on = staffIds.includes(p.id);
                  const label = p.displayName || p.name || 'Unnamed';
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleStaff(p.id)}
                      className={`px-3 py-1.5 rounded-full border text-sm transition-all ${
                        on ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {label}
                      {p.role ? <span className={`ml-1 text-[10px] ${on ? 'text-gray-300' : 'text-gray-400'}`}>{p.role}</span> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <SeatLimitField mode={scheduleMode} value={form.maxSeatsPerBooking} onChange={v => set('maxSeatsPerBooking', v)} />

      {(bookableType === 'facility' || bookableType === 'spa') && scheduleMode === 'timetable' && (
        <p className="text-[11px] text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
          Members can only book slots you've scheduled ahead of time. For all-day availability
          (e.g. courts 6am–10pm), create a recurring template and generate the slots in bulk.
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {isEdit ? 'Save Changes' : 'Create Bookable'}
        </button>
      </div>
    </form>
  );
}

// ─── ClassesPanel ──────────────────────────────────────────────────────────────

/**
 * The gym's member booking cut-off: members can book up to N minutes before a
 * session starts — timetable classes, appointments, courts and waitlists.
 * Bookables with their own notice keep it; staff are never blocked.
 */
function MemberCutoffBar({ gymId }: { gymId: string }) {
  const [value, setValue] = useState<string>('');
  const [saved, setSaved] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!gymId) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      fetch(`${API_BASE_URL}/api/classes/booking-settings?gymId=${gymId}`, { headers: { Authorization: `Bearer ${session.access_token}` } })
        .then(r => r.json())
        .then(d => { if (d.success) { setValue(String(d.data.memberBookingCutoffMin)); setSaved(String(d.data.memberBookingCutoffMin)); } })
        .catch(() => {});
    });
  }, [gymId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/classes/booking-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ gymId, memberBookingCutoffMin: parseInt(value) || 0 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSaved(String(data.data.memberBookingCutoffMin));
      setValue(String(data.data.memberBookingCutoffMin));
      toast.success('Booking cut-off saved');
    } catch (err: any) {
      toast.error(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!saved) return null;
  return (
    <div className="mx-4 sm:mx-6 mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
      <Clock className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="text-sm text-gray-700">Members can book up to</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={e => setValue(e.target.value)}
        className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
        aria-label="Member booking cut-off in minutes"
      />
      <span className="text-sm text-gray-700">minutes before a session starts</span>
      {value !== saved && (
        <button onClick={save} disabled={saving} className="px-3 py-1 bg-zinc-900 text-white text-xs font-medium rounded-lg hover:bg-zinc-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
      <span className="basis-full text-[11px] text-gray-400">Inside it, sessions are hidden from members. A bookable with its own notice keeps it; staff are never blocked.</span>
    </div>
  );
}

function ClassesPanel({
  gymId,
  search,
  showInactive,
  onShowClassForm,
  refreshSignal,
}: {
  gymId: string;
  search: string;
  showInactive: boolean;
  onShowClassForm: (gymClass: Partial<GymClass> | 'new') => void;
  refreshSignal?: number;
}) {
  const [classes, setClasses] = useState<GymClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchClasses = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (showInactive) params.set('includeInactive', 'true');

      const res = await fetch(`${API_BASE_URL}/api/classes?${params}`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'x-client-type': 'web',
        },
      });
      const data = await res.json();
      if (data.success) setClasses(data.data);
    } catch {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClasses(); }, [search, showInactive, refreshSignal]);

  const toggleActive = async (gymClass: GymClass) => {
    setTogglingId(gymClass.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch(`${API_BASE_URL}/api/classes/${gymClass.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          'x-client-type': 'web',
        },
        body: JSON.stringify({ isActive: !gymClass.isActive }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success(gymClass.isActive ? 'Class deactivated' : 'Class activated');
      fetchClasses();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  const levelColor: Record<string, string> = {
    Beginner: 'bg-green-100 text-green-700',
    Intermediate: 'bg-yellow-100 text-yellow-700',
    Advanced: 'bg-red-100 text-red-700',
    'All Levels': 'bg-blue-100 text-blue-700',
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="text-center py-16">
        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">No bookables yet</p>
        <p className="text-gray-400 text-sm mt-1">Create a class, spa slot, PT block, consult, or facility to start scheduling sessions</p>
        <button
          onClick={() => onShowClassForm('new')}
          className="mt-4 px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-xl hover:bg-zinc-700 transition-colors"
        >
          Create Bookable
        </button>
      </div>
    );
  }

  return (
    <>
    <MemberCutoffBar gymId={gymId} />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 sm:p-6">
      {classes.map((c, idx) => (
        <div
          key={c.id}
          className={`bg-white rounded-2xl border p-5 shadow-sm transition-all hover:shadow-md ${
            !c.isActive ? 'opacity-60 border-gray-100' : 'border-gray-100'
          }`}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-gray-900 truncate">{c.name}</h3>
                <TypeChip type={c.bookableType} className="shrink-0" />
              </div>
              {c.category && (
                <span className="text-xs text-gray-500 font-medium">{c.category}</span>
              )}
            </div>
            {!c.isActive && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-full shrink-0">
                Inactive
              </span>
            )}
          </div>

          {c.description && (
            <p className="text-xs text-gray-500 mb-3 line-clamp-2">{c.description}</p>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            {c.level && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${levelColor[c.level] || 'bg-gray-100 text-gray-600'}`}>
                {c.level}
              </span>
            )}
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              {c.defaultDurationMinutes}m
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Users className="w-3 h-3" />
              {c.defaultCapacity} spots
            </span>
            {c._count?.sessions != null && (
              <span className="text-xs text-gray-500">
                {c._count.sessions} upcoming
              </span>
            )}
          </div>

          {c.tags && c.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-4">
              {c.tags.slice(0, 3).map(tag => (
                <span key={tag} className="flex items-center gap-1 text-xs bg-gray-50 text-gray-600 px-2 py-0.5 rounded-full border border-gray-200">
                  <Tag className="w-2.5 h-2.5" />
                  {tag}
                </span>
              ))}
              {c.tags.length > 3 && (
                <span className="text-xs text-gray-400">+{c.tags.length - 3} more</span>
              )}
            </div>
          )}

          <div
            className="flex items-center justify-between pt-3 border-t border-gray-50"
            {...(idx === 0 ? { 'data-tour': 'tour-bookable-actions' } : {})}
          >
            <button
              onClick={() => onShowClassForm(c)}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
            <button
              onClick={() => toggleActive(c)}
              disabled={togglingId === c.id}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-60"
            >
              {togglingId === c.id
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                : c.isActive
                  ? <><ToggleRight className="w-4 h-4 text-green-500" /> Active</>
                  : <><ToggleLeft className="w-4 h-4" /> Inactive</>
              }
            </button>
          </div>
        </div>
      ))}
    </div>
    </>
  );
}

// ─── RoomsPanel (Resources tab) ────────────────────────────────────────────────

const ROOM_KINDS = [
  { value: 'room', label: 'Room' },
  { value: 'studio', label: 'Studio' },
  { value: 'facility', label: 'Facility' },
  { value: 'therapy', label: 'Therapy Room' },
];
const roomKindLabel = (kind?: string) => ROOM_KINDS.find(k => k.value === kind)?.label || 'Room';

function RoomsPanel({
  gymId,
  branches,
  onRoomsChange,
  externalShowCreate,
  onExternalShowCreate,
}: {
  gymId: string;
  branches: Branch[];
  onRoomsChange: (rooms: Room[]) => void;
  externalShowCreate?: boolean;
  onExternalShowCreate?: (v: boolean) => void;
}) {
  const [rooms, setRooms] = useState<BranchRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', capacity: '', kind: 'room' });
  const [form, setForm] = useState({ branchId: '', name: '', capacity: '', kind: 'room' });

  // Sync external create trigger
  useEffect(() => {
    if (externalShowCreate) {
      setShowCreate(true);
      onExternalShowCreate?.(false);
    }
  }, [externalShowCreate]);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const gymIds = [...new Set(branches.map(b => b.gymId).filter(Boolean) as string[])];
      const targetGymId = gymIds[0] || gymId;
      if (!targetGymId) return;
      const res = await fetch(
        `${API_BASE_URL}/api/classes/${targetGymId}/rooms?includeInactive=true`,
        { headers: { Authorization: `Bearer ${session.access_token}`, 'x-client-type': 'web' } }
      );
      const d = await res.json();
      if (d.success) {
        setRooms(d.data);
        onRoomsChange(d.data.filter((r: BranchRoom) => r.isActive));
      }
    } catch { toast.error('Failed to load rooms'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (gymId || branches.length) fetchRooms(); }, [gymId, branches]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.branchId || !form.name.trim()) return toast.error('Branch and name are required');
    const targets = form.branchId === ALL_BRANCHES ? branches : branches.filter(b => b.id === form.branchId);
    if (!targets.length) return toast.error('No branch to add the resource to');
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const results = await Promise.all(targets.map(async branch => {
        const res = await fetch(`${API_BASE_URL}/api/classes/rooms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            'x-client-type': 'web',
          },
          body: JSON.stringify({
            gymId: branch.gymId || gymId,
            branchId: branch.id,
            name: form.name.trim(),
            kind: form.kind,
            capacity: form.capacity ? parseInt(form.capacity) : null,
          }),
        });
        const d = await res.json();
        return { branch, ok: res.ok && d.success, error: d.error as string | undefined };
      }));
      const failed = results.filter(r => !r.ok);
      if (failed.length === results.length) throw new Error(failed[0].error || 'Failed');
      if (failed.length) {
        toast.error(`Could not add in ${failed.map(f => f.branch.displayName || f.branch.name).join(', ')}`);
      }
      toast.success(results.length > 1 ? `Resource added to ${results.length - failed.length} branches` : 'Resource created');
      setForm({ branchId: '', name: '', capacity: '', kind: 'room' });
      setShowCreate(false);
      fetchRooms();
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (roomId: string) => {
    if (!editForm.name.trim()) return toast.error('Name is required');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/classes/rooms/${roomId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          'x-client-type': 'web',
        },
        body: JSON.stringify({
          name: editForm.name.trim(),
          kind: editForm.kind,
          capacity: editForm.capacity ? parseInt(editForm.capacity) : null,
        }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error);
      toast.success('Resource updated');
      setEditingId(null);
      fetchRooms();
    } catch (err: any) { toast.error(err.message || 'Failed'); }
  };

  const handleToggle = async (room: BranchRoom) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch(`${API_BASE_URL}/api/classes/rooms/${room.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          'x-client-type': 'web',
        },
        body: JSON.stringify({ isActive: !room.isActive }),
      });
      fetchRooms();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (roomId: string) => {
    if (!confirm('Delete this room? Sessions using this room will keep their reference but new ones won\'t be assignable.')) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/classes/rooms/${roomId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}`, 'x-client-type': 'web' },
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error);
      toast.success('Room deleted');
      fetchRooms();
    } catch (err: any) { toast.error(err.message || 'Failed'); }
  };

  const byBranch = branches.map(b => ({
    branch: b,
    rooms: rooms.filter(r => r.branchId === b.id),
  })).filter(g => g.rooms.length > 0 || branches.length <= 2);

  return (
    <div className="flex-1 overflow-auto p-4 sm:p-6 space-y-5">
      <p className="text-sm text-gray-500">Manage rooms, studios, facilities and therapy rooms within each branch. Facility and spa sessions are scheduled into these resources.</p>

      {showCreate && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">New Resource</h3>
            <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <form onSubmit={handleCreate} className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="rt-label">Branch *</label>
              <select
                value={form.branchId}
                onChange={e => setForm(f => ({ ...f, branchId: e.target.value }))}
                className="rt-input"
                required
              >
                <option value="">— Select —</option>
                {branches.length > 1 && <option value={ALL_BRANCHES}>All branches ({branches.length})</option>}
                {branches.map(b => <option key={b.id} value={b.id}>{b.displayName || b.name}</option>)}
              </select>
              {form.branchId === ALL_BRANCHES && (
                <p className="text-[11px] text-gray-500 mt-1">Creates one resource with this name in every branch.</p>
              )}
            </div>
            <div className="col-span-2 sm:col-span-2">
              <label className="rt-label">Name *</label>
              <input
                type="text"
                placeholder="e.g. Studio A, Badminton Court 1"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="rt-input"
                required
              />
            </div>
            <div>
              <label className="rt-label">Type</label>
              <select
                value={form.kind}
                onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}
                className="rt-input"
              >
                {ROOM_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <label className="rt-label">Capacity</label>
              <input
                type="number"
                min={1}
                placeholder="Optional"
                value={form.capacity}
                onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
                className="rt-input"
              />
            </div>
            <div className="col-span-2 sm:col-span-5 flex gap-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white text-sm font-semibold rounded-xl hover:bg-zinc-700 transition-colors disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Resource
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="px-5 py-2.5 text-sm text-gray-600 hover:text-gray-900 font-medium">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : rooms.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <DoorOpen className="w-10 h-10 text-gray-200 mb-4" />
          <p className="font-bold text-gray-400">No resources yet</p>
          <p className="text-sm text-gray-300 mt-1">Add rooms, studios, courts or therapy rooms to assign them to sessions</p>
        </div>
      ) : (
        <div className="space-y-5">
          {byBranch.map(({ branch, rooms: branchRooms }) => (
            <div key={branch.id}>
              <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2 px-1">
                {branch.displayName || branch.name}
              </p>
              {branchRooms.length === 0 ? (
                <p className="text-sm text-gray-300 px-1">No rooms for this branch</p>
              ) : (
                <div className="space-y-2">
                  {branchRooms.map(room => (
                    <div
                      key={room.id}
                      className={`bg-white border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 transition-opacity ${!room.isActive ? 'opacity-50' : ''}`}
                    >
                      {editingId === room.id ? (
                        <div className="flex flex-1 items-center gap-3">
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                          />
                          <select
                            value={editForm.kind}
                            onChange={e => setEditForm(f => ({ ...f, kind: e.target.value }))}
                            className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
                          >
                            {ROOM_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                          </select>
                          <input
                            type="number"
                            min={1}
                            placeholder="Capacity"
                            value={editForm.capacity}
                            onChange={e => setEditForm(f => ({ ...f, capacity: e.target.value }))}
                            className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                          />
                          <button
                            onClick={() => handleUpdate(room.id)}
                            className="px-3 py-1.5 text-xs font-bold bg-zinc-900 text-white rounded-lg hover:bg-zinc-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center shrink-0">
                            <DoorOpen className="w-5 h-5 text-zinc-500" />
                          </div>
                          <div>
                            <p className="font-bold text-sm text-gray-900">{room.name}</p>
                            <p className="text-xs text-gray-500">
                              {roomKindLabel(room.kind)}
                              {room.capacity ? ` · Capacity ${room.capacity}` : ''}
                              {!room.isActive && <span className="ml-2 text-orange-500">· Inactive</span>}
                            </p>
                          </div>
                        </div>
                      )}
                      {editingId !== room.id && (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => { setEditingId(room.id); setEditForm({ name: room.name, capacity: room.capacity?.toString() || '', kind: room.kind || 'room' }); }}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggle(room)}
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${room.isActive ? 'text-green-600 bg-green-50 border-green-100 hover:bg-green-100' : 'text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
                          >
                            {room.isActive ? 'Active' : 'Inactive'}
                          </button>
                          <button
                            onClick={() => handleDelete(room.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <style>{`
        .rt-label { display:block; font-size:10px; font-weight:900; text-transform:uppercase; letter-spacing:.08em; color:#9ca3af; margin-bottom:5px; }
        .rt-input { width:100%; padding:8px 12px; border-radius:10px; border:1px solid #e5e7eb; font-size:13px; outline:none; background:#f9fafb; }
        .rt-input:focus { border-color:#6b7280; background:white; }
      `}</style>
    </div>
  );
}

// ─── RecurringTemplatesPanel ───────────────────────────────────────────────────

const DAYS_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Generation guard rails — mirror MAX_GENERATE_* in routes/recurringTemplates.ts
const MAX_GENERATE_WEEKS = 12;
const MAX_GENERATE_SESSIONS = 250;
// Mirrors MAX_SLOT_GAP_MINUTES in services/availabilityService.ts
const MAX_SLOT_GAP_MINUTES = 240;

/** Dates in [startDate, startDate + weeks*7] falling on one of daysOfWeek. */
function countGenerationDays(startDate: string, weeksAhead: number, daysOfWeek: number[]): number {
  if (!startDate || !weeksAhead || !daysOfWeek.length) return 0;
  const start = new Date(startDate + 'T00:00:00');
  if (Number.isNaN(start.getTime())) return 0;
  const end = new Date(start);
  end.setDate(end.getDate() + weeksAhead * 7);
  const days = new Set(daysOfWeek);
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    if (days.has(cursor.getDay())) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

// HH:MM → minutes since midnight (mirrors the server's day-fill slot math)
const hhmmToMinutes = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

function RecurringTemplatesPanel({
  gymId,
  classes,
  trainers,
  branches,
  services,
  rooms,
  externalShowCreate,
  onExternalShowCreate,
}: {
  gymId: string;
  classes: GymClass[];
  trainers: Trainer[];
  branches: Branch[];
  services: Service[];
  rooms: Room[];
  externalShowCreate?: boolean;
  onExternalShowCreate?: (v: boolean) => void;
}) {
  const [templates, setTemplates] = useState<RecurringTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [savingCreate, setSavingCreate] = useState(false);

  // Generate panel state — opened per-template
  const [generateTarget, setGenerateTarget] = useState<RecurringTemplate | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genForm, setGenForm] = useState({
    daysOfWeek: [] as number[],
    startTime: '08:00',
    endTime: '09:00',
    startDate: new Date().toISOString().slice(0, 10),
    weeksAhead: '4',
    // Day-fill: back-to-back slots across [startTime, endTime) minus breaks,
    // with gapMinutes left free after each slot
    fillDay: false,
    slotMinutes: '60',
    gapMinutes: '0',
    breaks: [] as Array<{ start: string; end: string }>,
  });

  // Template create form — WHO/WHAT/WHERE only
  const [form, setForm] = useState({
    classId: '', branchId: '', trainerId: '', roomId: '', serviceId: '', capacity: '20',
  });
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (externalShowCreate) { setShowCreate(true); onExternalShowCreate?.(false); }
  }, [externalShowCreate]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/recurring-templates`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const d = await res.json();
      if (d.success) setTemplates(d.data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (gymId) fetchTemplates(); }, [gymId]);

  const allBranchesForm = form.branchId === ALL_BRANCHES;
  const formTrainers = trainersForBranch(trainers, form.branchId);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.classId || !form.branchId) return toast.error('Class and branch are required');
    const targets = allBranchesForm ? branches : branches.filter(b => b.id === form.branchId);
    if (!targets.length) return toast.error('No branch to create the template in');
    setSavingCreate(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const results = await Promise.all(targets.map(async branch => {
        const res = await fetch(`${API_BASE_URL}/api/recurring-templates`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            gymId: branch.gymId || gymId,
            classId: form.classId,
            branchId: branch.id,
            // A provider or room belongs to one branch — a per-branch template
            // starts unassigned and is filled in from the template list.
            trainerId: allBranchesForm ? null : (form.trainerId || null),
            roomId: allBranchesForm ? null : (form.roomId || null),
            serviceId: form.serviceId || null,
            capacity: parseInt(form.capacity) || 20,
          }),
        });
        const d = await res.json();
        return { branch, ok: res.ok && d.success, error: d.error as string | undefined };
      }));
      const failed = results.filter(r => !r.ok);
      if (failed.length === results.length) throw new Error(failed[0].error || 'Failed');
      if (failed.length) {
        toast.error(`Could not create in ${failed.map(f => f.branch.displayName || f.branch.name).join(', ')}`);
      }
      toast.success(
        results.length > 1
          ? `${results.length - failed.length} templates created — use Generate on each to schedule sessions`
          : 'Template created — use Generate to schedule sessions'
      );
      setShowCreate(false);
      setForm({ classId: '', branchId: '', trainerId: '', roomId: '', serviceId: '', capacity: '20' });
      fetchTemplates();
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    finally { setSavingCreate(false); }
  };

  const toggleDay = (day: number) => {
    setGenForm(f => ({
      ...f,
      daysOfWeek: f.daysOfWeek.includes(day)
        ? f.daysOfWeek.filter(d => d !== day)
        : [...f.daysOfWeek, day].sort(),
    }));
  };

  // Live day-fill preview — mirrors the server's slotTimesForDay: slots of
  // `step` minutes across [startTime, endTime) with `gap` minutes free after
  // each; a slot that overlaps a break (t < break.end && t + step > break.start)
  // is skipped and the run restarts at the break's end.
  const genClassDuration = generateTarget
    ? (classes.find(c => c.id === generateTarget.classId)?.defaultDurationMinutes || 60)
    : 60;
  const genGap = Math.max(0, parseInt(genForm.gapMinutes) || 0);
  const gapTooLong = genGap > MAX_SLOT_GAP_MINUTES;
  const fillSlotsPerDay = useMemo(() => {
    if (!genForm.fillDay || !genForm.startTime || !genForm.endTime) return 0;
    const step = Math.max(5, parseInt(genForm.slotMinutes) || genClassDuration);
    const rangeStart = hhmmToMinutes(genForm.startTime);
    const rangeEnd = hhmmToMinutes(genForm.endTime);
    const breaks = genForm.breaks
      .filter(b => b.start && b.end)
      .map(b => ({ start: hhmmToMinutes(b.start), end: hhmmToMinutes(b.end) }));
    let count = 0;
    let t = rangeStart;
    while (t + step <= rangeEnd) {
      // A slot that would run into a break is skipped; slots start again when the break ends.
      const inBreak = breaks.filter(b => t < b.end && t + step > b.start);
      if (inBreak.length) { t = Math.max(...inBreak.map(b => b.end)); continue; }
      count++;
      t += step + genGap;
    }
    return count;
  }, [genForm.fillDay, genForm.startTime, genForm.endTime, genForm.slotMinutes, genGap, genForm.breaks, genClassDuration]);

  const weeksAhead = Math.max(0, parseInt(genForm.weeksAhead) || 0);
  const slotsPerDay = genForm.fillDay ? fillSlotsPerDay : 1;
  const plannedSessions = countGenerationDays(genForm.startDate, weeksAhead, genForm.daysOfWeek) * slotsPerDay;
  const weeksTooMany = weeksAhead < 1 || weeksAhead > MAX_GENERATE_WEEKS;
  const tooManySessions = plannedSessions > MAX_GENERATE_SESSIONS;

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!generateTarget) return;
    if (!genForm.daysOfWeek.length) return toast.error('Select at least one day');
    if (!genForm.startTime || !genForm.endTime) return toast.error('Start and end time are required');
    if (!genForm.startDate) return toast.error('Start date is required');
    if (genForm.fillDay && gapTooLong) return toast.error(`The gap between slots can be at most ${MAX_SLOT_GAP_MINUTES} minutes`);
    if (genForm.fillDay && fillSlotsPerDay === 0) return toast.error('Day range is too short for even one slot');
    if (weeksTooMany) return toast.error(`Generate between 1 and ${MAX_GENERATE_WEEKS} weeks at a time`);
    if (tooManySessions) {
      return toast.error(`That would create about ${plannedSessions} sessions — the limit is ${MAX_GENERATE_SESSIONS} per generate`);
    }
    setGenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}/api/recurring-templates/${generateTarget.id}/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          daysOfWeek: genForm.daysOfWeek,
          startTime: genForm.startTime,
          endTime: genForm.endTime,
          startDate: genForm.startDate,
          weeksAhead,
          ...(genForm.fillDay
            ? {
                fillDay: true,
                ...(parseInt(genForm.slotMinutes) ? { slotMinutes: parseInt(genForm.slotMinutes) } : {}),
                ...(genGap > 0 ? { gapMinutes: genGap } : {}),
                breaks: genForm.breaks.filter(b => b.start && b.end),
              }
            : {}),
        }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error);
      toast.success(`${d.data.created} session${d.data.created !== 1 ? 's' : ''} scheduled`);
      setGenerateTarget(null);
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    finally { setGenerating(false); }
  };

  const handleToggle = async (t: RecurringTemplate) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch(`${API_BASE_URL}/api/recurring-templates/${t.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !t.isActive }),
      });
      fetchTemplates();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this recurring template? Existing sessions will not be affected.')) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch(`${API_BASE_URL}/api/recurring-templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="flex-1 overflow-auto p-4 sm:p-6 space-y-5">
      <p className="text-sm text-gray-500">Templates define the class, branch, trainer, and room. Use <strong>Generate</strong> to pick days, times, and date range to create actual sessions.</p>

      {/* Create template form */}
      {showCreate && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">New Template</h3>
            <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <form onSubmit={handleCreate} className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="rt-label">Class *</label>
              <select value={form.classId} onChange={e => set('classId', e.target.value)} className="rt-input" required>
                <option value="">— Select —</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="rt-label">Branch *</label>
              <select
                value={form.branchId}
                onChange={e => setForm(f => ({ ...f, branchId: e.target.value, trainerId: '', roomId: '' }))}
                className="rt-input"
                required
              >
                <option value="">— Select —</option>
                {branches.length > 1 && <option value={ALL_BRANCHES}>All branches ({branches.length})</option>}
                {branches.map(b => <option key={b.id} value={b.id}>{b.displayName || b.name}</option>)}
              </select>
              {allBranchesForm && (
                <p className="text-[11px] text-gray-500 mt-1">One template per branch.</p>
              )}
            </div>
            <div>
              <label className="rt-label">Capacity</label>
              <input type="number" min={1} value={form.capacity} onChange={e => set('capacity', e.target.value)} className="rt-input" />
            </div>
            <div>
              <label className="rt-label">Trainer</label>
              <select value={form.trainerId} onChange={e => set('trainerId', e.target.value)} className="rt-input" disabled={allBranchesForm}>
                <option value="">— None —</option>
                {formTrainers.map(t => <option key={t.id} value={t.id}>{t.displayName || t.name}</option>)}
              </select>
              {!allBranchesForm && form.branchId && formTrainers.length === 0 && (
                <p className="text-[11px] text-amber-600 mt-1">No staff attached to this branch yet.</p>
              )}
            </div>
            <div>
              <label className="rt-label">Room</label>
              <select value={form.roomId} onChange={e => set('roomId', e.target.value)} className="rt-input" disabled={allBranchesForm}>
                <option value="">— None —</option>
                {rooms.filter(r => !allBranchesForm && r.branchId === form.branchId).map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="rt-label">Service (pricing)</label>
              <select value={form.serviceId} onChange={e => set('serviceId', e.target.value)} className="rt-input">
                <option value="">— None —</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="col-span-2 sm:col-span-3 flex gap-3 pt-2">
              <button type="submit" disabled={savingCreate} className="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white text-sm font-semibold rounded-xl hover:bg-zinc-700 transition-colors disabled:opacity-50">
                {savingCreate && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Template
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="px-5 py-2.5 text-sm text-gray-600 hover:text-gray-900 font-medium">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Template list */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <CalendarDays className="w-10 h-10 text-gray-200 mb-4" />
          <p className="font-bold text-gray-400">No recurring templates</p>
          <p className="text-sm text-gray-300 mt-1">Create a template, then use Generate to schedule sessions</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((t, idx) => (
            <div key={t.id} className={`bg-white border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${!t.isActive ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center shrink-0">
                  <CalendarDays className="w-5 h-5 text-zinc-500" />
                </div>
                <div>
                  <p className="font-bold text-sm text-gray-900">{t.class.name}</p>
                  <p className="text-xs text-gray-500">
                    {t.branch.displayName || t.branch.name} · {t.capacity} cap
                    {!t.isActive && <span className="ml-2 text-orange-500">· Paused</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  {...(idx === 0 ? { 'data-tour': 'tour-generate-btn' } : {})}
                  onClick={() => {
                    setGenerateTarget(t);
                    const dur = classes.find(c => c.id === t.classId)?.defaultDurationMinutes || 60;
                    setGenForm({ daysOfWeek: [], startTime: '08:00', endTime: '09:00', startDate: new Date().toISOString().slice(0, 10), weeksAhead: '4', fillDay: false, slotMinutes: String(dur), gapMinutes: '0', breaks: [] });
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-600 bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Generate
                </button>
                <button
                  onClick={() => handleToggle(t)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${t.isActive ? 'text-green-600 bg-green-50 border-green-100 hover:bg-green-100' : 'text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
                >
                  {t.isActive ? 'Active' : 'Paused'}
                </button>
                <button onClick={() => handleDelete(t.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Generate slide panel */}
      <SlidePanel open={!!generateTarget} onClose={() => setGenerateTarget(null)} title={`Generate — ${generateTarget?.class.name || ''}`}>
        <form onSubmit={handleGenerate} className="p-6 space-y-6">
          {/* Days of week */}
          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Days of the week *</label>
            <div className="flex gap-2 flex-wrap">
              {DAYS_ABBR.map((day, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleDay(i)}
                  className={`w-12 h-12 rounded-xl text-xs font-bold transition-all border ${
                    genForm.daysOfWeek.includes(i)
                      ? 'bg-zinc-900 text-white border-zinc-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          {/* Day-fill toggle */}
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={genForm.fillDay}
              onChange={e => setGenForm(f => ({ ...f, fillDay: e.target.checked }))}
              className="accent-zinc-900 mt-0.5"
            />
            <span>
              <span className="block text-sm font-semibold text-gray-900">Fill the day with back-to-back slots</span>
              <span className="block text-xs text-gray-400 mt-0.5">e.g. steam room every 30 min from 09:00 to 21:30, or courts hourly</span>
            </span>
          </label>

          {/* Times */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">{genForm.fillDay ? 'Day starts *' : 'Start Time *'}</label>
              <input
                type="time"
                value={genForm.startTime}
                onChange={e => setGenForm(f => ({ ...f, startTime: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">{genForm.fillDay ? 'Day ends *' : 'End Time *'}</label>
              <input
                type="time"
                value={genForm.endTime}
                onChange={e => setGenForm(f => ({ ...f, endTime: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Day-fill options */}
          {genForm.fillDay && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Slot length (min)</label>
                  <input
                    type="number"
                    min={5}
                    max={480}
                    value={genForm.slotMinutes}
                    onChange={e => setGenForm(f => ({ ...f, slotMinutes: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Gap between slots (min)</label>
                  <input
                    type="number"
                    min={0}
                    max={MAX_SLOT_GAP_MINUTES}
                    step={5}
                    value={genForm.gapMinutes}
                    onChange={e => setGenForm(f => ({ ...f, gapMinutes: e.target.value }))}
                    className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent ${gapTooLong ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 -mt-2">
                {gapTooLong
                  ? <span className="text-red-600">At most {MAX_SLOT_GAP_MINUTES} minutes.</span>
                  : genGap > 0
                    ? `Each slot is followed by ${genGap} free minutes before the next one starts.`
                    : 'No gap — each slot starts as the one before it ends.'}
              </p>

              {/* Breaks editor */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-400">Breaks (no slots)</label>
                  <button
                    type="button"
                    onClick={() => setGenForm(f => ({ ...f, breaks: [...f.breaks, { start: '13:00', end: '14:00' }] }))}
                    className="text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 px-2.5 py-1 rounded-lg transition-colors"
                  >
                    + Add break
                  </button>
                </div>
                {genForm.breaks.length === 0 ? (
                  <p className="text-xs text-gray-400">No breaks — slots run across the whole range. After a break, slots start again when it ends.</p>
                ) : (
                  <div className="space-y-2">
                    {genForm.breaks.map((br, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="time"
                          value={br.start}
                          onChange={e => setGenForm(f => ({ ...f, breaks: f.breaks.map((x, j) => j === i ? { ...x, start: e.target.value } : x) }))}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                        />
                        <span className="text-sm text-gray-400">–</span>
                        <input
                          type="time"
                          value={br.end}
                          onChange={e => setGenForm(f => ({ ...f, breaks: f.breaks.map((x, j) => j === i ? { ...x, end: e.target.value } : x) }))}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                        />
                        <button
                          type="button"
                          onClick={() => setGenForm(f => ({ ...f, breaks: f.breaks.filter((_, j) => j !== i) }))}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-xs font-semibold text-gray-500">
                ≈ {fillSlotsPerDay} slot{fillSlotsPerDay !== 1 ? 's' : ''} per selected day
              </p>
            </>
          )}

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Start Date *</label>
              <input
                type="date"
                value={genForm.startDate}
                onChange={e => setGenForm(f => ({ ...f, startDate: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">
                Weeks to generate <span className="text-gray-300">(max {MAX_GENERATE_WEEKS})</span>
              </label>
              <input
                type="number"
                min={1}
                max={MAX_GENERATE_WEEKS}
                value={genForm.weeksAhead}
                onChange={e => setGenForm(f => ({ ...f, weeksAhead: e.target.value }))}
                className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent ${weeksTooMany ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              />
              {weeksTooMany && (
                <p className="text-[11px] text-red-600 mt-1">Between 1 and {MAX_GENERATE_WEEKS} weeks per generate.</p>
              )}
            </div>
          </div>

          {/* Preview summary */}
          {genForm.daysOfWeek.length > 0 && genForm.startDate && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
              Generating <strong>{DAYS_ABBR.filter((_, i) => genForm.daysOfWeek.includes(i)).join(', ')}</strong> {genForm.fillDay ? (genGap > 0 ? `slots ${genGap} min apart` : 'back-to-back slots') : 'sessions'}
              {genForm.fillDay ? ' across ' : ' at '}<strong>{genForm.startTime}–{genForm.endTime}</strong> for <strong>{genForm.weeksAhead} weeks</strong> from{' '}
              <strong>{new Date(genForm.startDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>.
              Up to <strong>{plannedSessions} sessions</strong> (skips duplicates).
            </div>
          )}

          {tooManySessions && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              That is over the <strong>{MAX_GENERATE_SESSIONS} session</strong> limit for one generate. Shorten the
              date range, pick fewer days{genForm.fillDay ? ', use longer slots' : ''}, then generate again for the
              next block.
            </div>
          )}

          <button
            type="submit"
            disabled={generating || !genForm.daysOfWeek.length || weeksTooMany || tooManySessions}
            className="w-full py-3 bg-zinc-900 text-white font-bold rounded-xl hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {generating && <Loader2 className="w-4 h-4 animate-spin" />}
            Generate Sessions
          </button>
        </form>
      </SlidePanel>

      <style>{`
        .rt-label { display:block; font-size:10px; font-weight:900; text-transform:uppercase; letter-spacing:.08em; color:#9ca3af; margin-bottom:5px; }
        .rt-input { width:100%; padding:8px 12px; border-radius:10px; border:1px solid #e5e7eb; font-size:13px; outline:none; background:#f9fafb; }
        .rt-input:focus { border-color:#6b7280; background:white; }
      `}</style>
    </div>
  );
}

// ─── QuickBookModal (front-desk Booking V2) ────────────────────────────────────

interface QuickBookMember {
  id: string;
  name?: string | null;
  fullName?: string | null;
  displayName?: string | null;
  phoneNo?: string | null;
  phoneNumber?: string | null;
}

const QB_PAYMENT_OPTIONS = [
  { key: 'auto', label: 'Use member entitlement (auto)' },
  { key: 'paid', label: 'Charge (force paid)' },
  { key: 'free', label: 'Free (comp)' },
] as const;

function QuickBookModal({ gymId, classes, branches, initial, onClose, onBooked, onCreateBookable }: {
  gymId: string;
  classes: GymClass[];
  branches: Branch[];
  /** Optional prefill (day-sheet click / find-next-available) */
  initial?: QuickBookPrefill;
  onClose: () => void;
  onBooked: () => void;
  onCreateBookable: () => void;
}) {
  const bookables = classes.filter(c =>
    c.isActive !== false && (c.scheduleMode === 'appointments' || c.scheduleMode === 'grid')
  );

  const [classId, setClassId] = useState(() =>
    initial?.classId && bookables.some(b => b.id === initial.classId) ? initial.classId : (bookables[0]?.id || '')
  );
  const [branchId, setBranchId] = useState(() =>
    initial?.branchId && branches.some(b => b.id === initial.branchId) ? initial.branchId : (branches[0]?.id || '')
  );
  const [date, setDate] = useState(initial?.date || toDateStr(new Date()));

  // Time/staff/room prefill is applied on the first availability load only —
  // any later change of bookable/branch/date clears the slot inputs as before.
  const prefillRef = useRef<QuickBookPrefill | undefined>(initial);

  const selectedClass = bookables.find(c => c.id === classId);
  const mode = selectedClass?.scheduleMode;

  // Member search (members list filters by `searchTerm`)
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState<QuickBookMember[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [member, setMember] = useState<QuickBookMember | null>(null);

  // Availability
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [apptAvail, setApptAvail] = useState<AppointmentAvailability | null>(null);
  const [gridAvail, setGridAvail] = useState<CourtGridAvailability | null>(null);

  // Appointment inputs
  const [startTime, setStartTime] = useState('');
  const [staffId, setStaffId] = useState('');
  const [roomId, setRoomId] = useState('');

  // Grid selection: courtId → sorted contiguous cell indexes
  const [gridSel, setGridSel] = useState<Record<string, number[]>>({});

  const [payment, setPayment] = useState<'auto' | 'paid' | 'free'>('auto');
  const [conflict, setConflict] = useState<{ code?: string; error: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!memberSearch.trim()) { setMemberResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data: { session: authSess } } = await supabase.auth.getSession();
        if (!authSess) return;
        const res = await fetch(
          `${API_BASE_URL}/api/members?searchTerm=${encodeURIComponent(memberSearch)}&limit=6${gymId ? `&gymId=${gymId}` : ''}`,
          { headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' } }
        );
        const data = await res.json();
        if (data.success) setMemberResults(data.data);
      } catch {}
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch, gymId]);

  // Load availability whenever the slot context changes
  useEffect(() => {
    const pre = prefillRef.current;
    prefillRef.current = undefined;
    setApptAvail(null); setGridAvail(null);
    setStartTime(pre?.startTime || ''); setStaffId(pre?.staffId || ''); setRoomId(pre?.roomId || '');
    setGridSel({}); setConflict(null);
    if (!classId || !branchId || !date || !mode) return;
    let cancelled = false;
    (async () => {
      setLoadingAvail(true);
      try {
        const { data: { session: authSess } } = await supabase.auth.getSession();
        if (!authSess) return;
        const res = await fetch(
          `${API_BASE_URL}/api/class-bookings/availability?classId=${classId}&branchId=${branchId}&date=${date}`,
          { headers: { Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' } }
        );
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load availability');
        if (mode === 'grid') {
          setGridAvail(data.data);
          // Pre-select the clicked court cell when prefilled from the day sheet
          if (pre?.startTime) {
            const grid = data.data as CourtGridAvailability;
            const court = (pre.roomId && grid.courts.find(c => c.id === pre.roomId))
              || grid.courts.find(c => c.cells.some(cc => cc.startTime === pre.startTime && cc.available));
            const idx = court ? court.cells.findIndex(cc => cc.startTime === pre.startTime && cc.available) : -1;
            if (court && idx >= 0) setGridSel({ [court.id]: [idx] });
          }
        }
        else setApptAvail(data.data);
      } catch (err: any) {
        if (!cancelled) toast.error(err.message || 'Failed to load availability');
      } finally {
        if (!cancelled) setLoadingAvail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classId, branchId, date, mode]);

  const addMinutes = (hhmm: string, mins: number) => {
    const [h, m] = hhmm.split(':').map(Number);
    const t = h * 60 + m + mins;
    return `${String(Math.floor(t / 60) % 24).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
  };

  // Click-to-select contiguous cells per court; clicking an end trims, clicking
  // elsewhere in a run clears it, clicking a detached cell starts over there.
  const toggleCell = (courtId: string, idx: number) => {
    setConflict(null);
    const cur = gridSel[courtId] || [];
    if (cur.includes(idx)) {
      if (idx === cur[0] || idx === cur[cur.length - 1]) {
        const next = cur.filter(i => i !== idx);
        const copy = { ...gridSel };
        if (next.length) copy[courtId] = next; else delete copy[courtId];
        setGridSel(copy);
      } else {
        const copy = { ...gridSel };
        delete copy[courtId];
        setGridSel(copy);
      }
      return;
    }
    if (!cur.length) {
      if (Object.keys(gridSel).length >= 4) return void toast.error('Up to 4 courts per booking');
      setGridSel({ ...gridSel, [courtId]: [idx] });
      return;
    }
    if (idx === cur[0] - 1) setGridSel({ ...gridSel, [courtId]: [idx, ...cur] });
    else if (idx === cur[cur.length - 1] + 1) setGridSel({ ...gridSel, [courtId]: [...cur, idx] });
    else setGridSel({ ...gridSel, [courtId]: [idx] });
  };

  const handleSubmit = async () => {
    if (!selectedClass) return void toast.error('Pick a bookable');
    if (!branchId || !date) return void toast.error('Branch and date are required');
    if (!member) return void toast.error('Pick a member');
    const resolvedGymId = branches.find(b => b.id === branchId)?.gymId || selectedClass.gymId || gymId;
    const payFlags = payment === 'paid' ? { forcePaid: true } : payment === 'free' ? { free: true } : {};

    let path: string;
    let body: any;
    if (mode === 'grid') {
      const selections = Object.entries(gridSel).map(([rid, cells]) => ({
        roomId: rid,
        startTime: gridAvail?.courts.find(c => c.id === rid)?.cells[cells[0]]?.startTime || '',
        cells: cells.length,
      }));
      if (!selections.length) return void toast.error('Select at least one slot on the grid');
      const minC = gridAvail?.minDurationCells ?? selectedClass.minDurationCells ?? 1;
      const maxC = gridAvail?.maxDurationCells ?? selectedClass.maxDurationCells ?? null;
      for (const s of selections) {
        if (s.cells < minC) return void toast.error(`Minimum booking is ${minC} cell${minC !== 1 ? 's' : ''} per court`);
        if (maxC && s.cells > maxC) return void toast.error(`Maximum booking is ${maxC} cell${maxC !== 1 ? 's' : ''} per court`);
      }
      path = 'court-grid';
      body = { gymId: resolvedGymId, branchId, memberId: member.id, classId: selectedClass.id, date, selections, ...payFlags };
    } else {
      if (!startTime) return void toast.error('Pick a start time');
      path = 'appointment';
      body = {
        gymId: resolvedGymId, branchId, memberId: member.id, classId: selectedClass.id, date, startTime,
        staffId: staffId || undefined,
        roomId: roomId || undefined,
        ...payFlags,
      };
    }

    setConflict(null);
    setSubmitting(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess) return;
      const res = await fetch(`${API_BASE_URL}/api/class-bookings/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authSess.access_token}`, 'x-client-type': 'web' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409) { setConflict({ code: data.code, error: data.error || 'Slot conflict' }); return; }
        throw new Error(data.error || 'Booking failed');
      }
      toast.success('Booked');
      onBooked();
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  // No appointment/grid bookables yet — explain and point to creating one
  if (bookables.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-900">Quick book</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-600">
            Quick book works with bookables in <strong>Appointments</strong> or <strong>Court grid</strong> mode.
            None of your bookables use those modes yet — set the <strong>Booking mode</strong> when creating or
            editing a bookable.
          </p>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              Close
            </button>
            <button onClick={onCreateBookable} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-zinc-900 rounded-xl hover:bg-zinc-700 transition-colors">
              Create a bookable
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-4 h-4 text-emerald-500" />
            Quick book
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Bookable */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bookable *</label>
          <select
            value={classId}
            onChange={e => setClassId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
          >
            {bookables.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} · {typeMeta(c.bookableType).label} · {c.scheduleMode === 'grid' ? 'Court grid' : 'Appointments'}
              </option>
            ))}
          </select>
        </div>

        {/* Branch + date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Branch *</label>
            <select
              value={branchId}
              onChange={e => setBranchId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
            >
              {branches.map(b => <option key={b.id} value={b.id}>{b.displayName || b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            />
          </div>
        </div>

        {/* Member */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Member *</label>
          {member ? (
            <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{member.displayName || member.name || member.fullName}</p>
                {(member.phoneNo || member.phoneNumber) && (
                  <p className="text-xs text-gray-500">{member.phoneNo || member.phoneNumber}</p>
                )}
              </div>
              <button
                onClick={() => { setMember(null); setMemberSearch(''); setMemberResults([]); }}
                className="p-1.5 text-gray-400 hover:text-gray-600 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or phone…"
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
                {searchLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
              </div>
              {memberResults.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm max-h-44 overflow-y-auto">
                  {memberResults.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => { setMember(m); setConflict(null); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 flex items-center justify-between gap-2"
                    >
                      <span className="font-medium text-gray-800 truncate">{m.displayName || m.name || m.fullName}</span>
                      {(m.phoneNo || m.phoneNumber) && (
                        <span className="text-xs text-gray-400 shrink-0">{m.phoneNo || m.phoneNumber}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Availability */}
        {loadingAvail ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : mode === 'appointments' && apptAvail ? (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700">Suggested times</label>
                <span className="text-[11px] text-gray-400">{apptAvail.durationMinutes} min</span>
              </div>
              {apptAvail.times.length === 0 ? (
                <p className="text-sm text-gray-400">No computed times for this day — you can still type any time below.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
                  {apptAvail.times.map(t => (
                    <button
                      key={t.startTime}
                      type="button"
                      onClick={() => { setStartTime(t.startTime); setConflict(null); }}
                      className={`px-2.5 py-1 rounded-lg border text-xs font-semibold transition-colors ${
                        startTime === t.startTime
                          ? 'bg-zinc-900 text-white border-zinc-900'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      {t.startTime}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={e => { setStartTime(e.target.value); setConflict(null); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
                <p className="text-[11px] text-gray-400 mt-1">Any minute works — phone bookings</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff</label>
                <select
                  value={staffId}
                  onChange={e => { setStaffId(e.target.value); setConflict(null); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
                >
                  <option value="">Auto / Unassigned</option>
                  {apptAvail.staff.map(s => (
                    <option key={s.id} value={s.id}>{s.name}{s.role ? ` (${s.role})` : ''}</option>
                  ))}
                </select>
              </div>
              {apptAvail.rooms.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room (optional)</label>
                  <select
                    value={roomId}
                    onChange={e => { setRoomId(e.target.value); setConflict(null); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
                  >
                    <option value="">No specific room</option>
                    {apptAvail.rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>
        ) : mode === 'grid' && gridAvail ? (
          gridAvail.courts.length === 0 ? (
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              No active courts in this branch — add courts in the Resources tab first.
            </p>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700">Pick slots (contiguous cells, up to 4 courts)</label>
                <span className="text-[11px] text-gray-400">{gridAvail.cellMinutes} min/cell</span>
              </div>
              <div className="overflow-x-auto border border-gray-200 rounded-xl max-h-72 overflow-y-auto">
                <table className="text-xs w-full">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-gray-400 font-semibold sticky left-0 bg-white">Time</th>
                      {gridAvail.courts.map(c => (
                        <th key={c.id} className="px-2 py-1.5 text-gray-600 font-bold whitespace-nowrap">{c.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(gridAvail.courts[0]?.cells || []).map((cell, rowIdx) => (
                      <tr key={cell.startTime} className="border-t border-gray-100">
                        <td className="px-2 py-1 text-gray-500 font-medium sticky left-0 bg-white whitespace-nowrap">{cell.startTime}</td>
                        {gridAvail.courts.map(court => {
                          const cc = court.cells[rowIdx];
                          const selected = (gridSel[court.id] || []).includes(rowIdx);
                          return (
                            <td key={court.id} className="px-1 py-0.5">
                              <button
                                type="button"
                                disabled={!cc?.available}
                                onClick={() => toggleCell(court.id, rowIdx)}
                                className={`w-full h-6 min-w-[52px] rounded-md border transition-colors ${
                                  selected
                                    ? 'bg-emerald-600 border-emerald-600'
                                    : cc?.available
                                      ? 'bg-white border-gray-200 hover:border-emerald-400 hover:bg-emerald-50'
                                      : 'bg-gray-100 border-gray-100 cursor-not-allowed'
                                }`}
                                title={cc?.available ? `${court.name} · ${cc.startTime}` : 'Unavailable'}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {Object.keys(gridSel).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {Object.entries(gridSel).map(([rid, cells]) => {
                    const court = gridAvail.courts.find(c => c.id === rid);
                    const start = court?.cells[cells[0]]?.startTime;
                    if (!court || !start) return null;
                    return (
                      <span key={rid} className="text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                        {court.name}: {start}–{addMinutes(start, cells.length * gridAvail.cellMinutes)}
                      </span>
                    );
                  })}
                </div>
              )}
              {(gridAvail.minDurationCells > 1 || gridAvail.maxDurationCells) && (
                <p className="text-[11px] text-gray-400 mt-1.5">
                  {gridAvail.minDurationCells > 1 && `Min ${gridAvail.minDurationCells} cells per court. `}
                  {gridAvail.maxDurationCells && `Max ${gridAvail.maxDurationCells} cells per court.`}
                </p>
              )}
            </div>
          )
        ) : null}

        {/* Payment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Payment</label>
          <div className="flex flex-col sm:flex-row gap-2">
            {QB_PAYMENT_OPTIONS.map(p => (
              <label
                key={p.key}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer transition-colors ${
                  payment === p.key
                    ? 'border-zinc-900 bg-zinc-50 font-semibold text-gray-900'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="qb-payment"
                  checked={payment === p.key}
                  onChange={() => setPayment(p.key)}
                  className="accent-zinc-900"
                />
                {p.label}
              </label>
            ))}
          </div>
        </div>

        {/* Conflict (409) shown inline */}
        {conflict && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div className="text-sm text-red-700">
              <p className="font-semibold">{conflict.error}</p>
              {conflict.code && <p className="text-[11px] text-red-400 mt-0.5">{conflict.code}</p>}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Book
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Learning Tour ─────────────────────────────────────────────────────────────

type TourTab = 'bookables' | 'calendar' | 'recurring' | 'resources';
type TourPlacement = 'bottom' | 'top' | 'left' | 'right' | 'center';
type TourLang = 'en' | 'hi' | 'te';

interface TourStep {
  tab: TourTab;
  target: string | null;
  placement: TourPlacement;
  icon: React.ReactNode;
}

const TOUR_STEPS: TourStep[] = [
  { tab: 'bookables', target: 'tour-new-bookable',     placement: 'bottom', icon: <BookOpen  className="w-5 h-5 text-orange-400" /> },
  { tab: 'bookables', target: null,                    placement: 'center', icon: <Tag       className="w-5 h-5 text-lime-400"   /> },
  { tab: 'bookables', target: 'tour-bookable-actions', placement: 'top',    icon: <Pencil    className="w-5 h-5 text-blue-400"   /> },
  { tab: 'calendar',  target: 'tour-new-session',      placement: 'bottom', icon: <CalendarDays className="w-5 h-5 text-blue-400" /> },
  { tab: 'calendar',  target: 'tour-today-cell',       placement: 'bottom', icon: <UserCheck className="w-5 h-5 text-emerald-400" /> },
  { tab: 'recurring', target: 'tour-new-template',     placement: 'bottom', icon: <Repeat2   className="w-5 h-5 text-purple-400" /> },
  { tab: 'recurring', target: 'tour-generate-btn',     placement: 'left',   icon: <Plus      className="w-5 h-5 text-purple-400" /> },
  { tab: 'resources', target: 'tour-add-resource',     placement: 'bottom', icon: <DoorOpen  className="w-5 h-5 text-teal-400"   /> },
  { tab: 'bookables', target: null,                    placement: 'center', icon: <Tag       className="w-5 h-5 text-violet-400" /> },
];

const TOUR_TEXT: Record<TourLang, { title: string; body: string }[]> = {
  en: [
    { title: 'Create bookables',          body: 'Start here — define everything members can book: classes, spa & recovery, personal training, consults, and facilities. Each bookable sets the default duration, capacity, and cancellation window.' },
    { title: 'Pick the right type',       body: 'The first choice when creating a bookable is its type. Class = group session (capacity 20). Spa, Personal and Consult = 1:1 slots. Facility = a self-service resource (court, steam room, pool, net) with no staff. Picking a type presets sensible defaults — you can change every one of them.' },
    { title: 'Edit & toggle bookables',   body: 'Edit updates details. The toggle deactivates a bookable without deleting it — all session history is preserved.' },
    { title: 'Schedule a session',        body: 'Add a one-off session here — pick the bookable, branch, trainer, room/court, and time slot.' },
    { title: 'Sessions & check-ins',      body: 'Tap any date to schedule. Click a session card to manage bookings, check members in (opens 15 min before start), or mark the session done.' },
    { title: 'Create a template',         body: 'A template stores the bookable, branch, trainer, and room — but no timing. Facilities and spa slots MUST be generated ahead this way: members can only book slots that already exist.' },
    { title: 'Generate sessions in bulk', body: 'Choose days of the week, a time slot, start date, and weeks ahead. Existing sessions are skipped automatically — no duplicates. Perfect for facility slots 6am–10pm.' },
    { title: 'Add resources',             body: 'Create spaces per branch — rooms, studios, facilities, therapy rooms. Facility bookings need a facility resource; spa sessions like a therapy room. Assigning them avoids double-booking.' },
    { title: 'Charge via Services',       body: 'In the Services section, create a priced service (e.g. Drop-in ₹500, Court hour ₹300). Link it when scheduling so members without a plan or pack are billed automatically on booking.' },
  ],
  hi: [
    { title: 'बुक करने योग्य चीज़ें बनाएं',   body: 'यहाँ से शुरू करें — सदस्य जो भी बुक कर सकते हैं वह बनाएं: क्लास, स्पा और रिकवरी, पर्सनल ट्रेनिंग, कंसल्ट और कोर्ट। हर बुकेबल में डिफ़ॉल्ट अवधि, क्षमता और कैंसिलेशन विंडो सेट होती है।' },
    { title: 'सही प्रकार चुनें',              body: 'बुकेबल बनाते समय सबसे पहला चुनाव उसका प्रकार है। Class = ग्रुप सेशन (क्षमता 20)। Spa, Personal और Consult = 1:1 स्लॉट। Court = बुक करने योग्य कोर्ट/सुविधा। प्रकार चुनने से उचित डिफ़ॉल्ट सेट हो जाते हैं — हर एक को बदला जा सकता है।' },
    { title: 'बुकेबल संपादित और टॉगल करें',  body: 'Edit से जानकारी अपडेट करें। टॉगल से बिना हटाए निष्क्रिय करें — सभी सेशन इतिहास सुरक्षित रहता है।' },
    { title: 'सेशन शेड्यूल करें',            body: 'यहाँ एक बार का सेशन जोड़ें — बुकेबल, शाखा, ट्रेनर, कमरा/कोर्ट और समय चुनें।' },
    { title: 'सेशन और चेक-इन',              body: 'किसी भी तारीख पर टैप करके सेशन बनाएं। सेशन कार्ड पर क्लिक करके बुकिंग प्रबंधित करें, सदस्यों को चेक-इन करें (शुरुआत से 15 मिनट पहले खुलता है) या सेशन पूर्ण करें।' },
    { title: 'टेम्पलेट बनाएं',              body: 'टेम्पलेट में बुकेबल, शाखा, ट्रेनर और कमरा सेव होता है — समय नहीं। कोर्ट और स्पा स्लॉट इसी तरीके से पहले से जेनरेट करने होते हैं: सदस्य केवल वही स्लॉट बुक कर सकते हैं जो पहले से मौजूद हैं।' },
    { title: 'एक साथ कई सेशन बनाएं',        body: 'सप्ताह के दिन, समय, शुरुआत की तारीख और हफ्तों की संख्या चुनें। मौजूदा सेशन अपने आप स्किप हो जाते हैं — कोई डुप्लीकेट नहीं। सुबह 6 से रात 10 के कोर्ट स्लॉट के लिए बिल्कुल सही।' },
    { title: 'संसाधन जोड़ें',               body: 'हर शाखा में जगहें बनाएं — कमरे, स्टूडियो, कोर्ट, थेरेपी रूम। कोर्ट बुकिंग के लिए कोर्ट ज़रूरी है; स्पा के लिए थेरेपी रूम बेहतर। असाइन करने से डबल-बुकिंग नहीं होती।' },
    { title: 'सेवाओं के ज़रिए भुगतान लें',  body: 'Services सेक्शन में कीमत वाली सेवा बनाएं (जैसे Drop-in ₹500, कोर्ट घंटा ₹300)। शेड्यूल करते समय लिंक करें ताकि बिना प्लान या पैक वाले सदस्यों से बुकिंग पर स्वतः शुल्क लिया जाए।' },
  ],
  te: [
    { title: 'బుక్ చేయదగినవి సృష్టించండి',        body: 'ఇక్కడ నుండి ప్రారంభించండి — సభ్యులు బుక్ చేయగలిగేవన్నీ సృష్టించండి: క్లాస్‌లు, స్పా & రికవరీ, పర్సనల్ ట్రైనింగ్, కన్సల్ట్‌లు మరియు కోర్టులు. ప్రతిదానికి డిఫాల్ట్ వ్యవధి, సామర్థ్యం మరియు రద్దు గడువు సెట్ అవుతాయి.' },
    { title: 'సరైన రకం ఎంచుకోండి',                body: 'బుకబుల్ సృష్టించేటప్పుడు మొదటి ఎంపిక దాని రకం. Class = గ్రూప్ సెషన్ (సామర్థ్యం 20). Spa, Personal, Consult = 1:1 స్లాట్లు. Court = బుక్ చేయదగిన కోర్టు/సదుపాయం. రకం ఎంచుకుంటే సరైన డిఫాల్ట్‌లు సెట్ అవుతాయి — ప్రతిదాన్ని మార్చుకోవచ్చు.' },
    { title: 'సవరించండి మరియు టోగుల్ చేయండి',    body: 'Edit ద్వారా వివరాలు అప్‌డేట్ చేయండి. టోగుల్ ద్వారా తొలగించకుండా నిష్క్రియం చేయండి — అన్ని సెషన్ చరిత్ర భద్రంగా ఉంటుంది.' },
    { title: 'సెషన్ షెడ్యూల్ చేయండి',             body: 'ఇక్కడ ఒక్కసారి సెషన్ జోడించండి — బుకబుల్, శాఖ, ట్రైనర్, గది/కోర్టు మరియు సమయం ఎంచుకోండి.' },
    { title: 'సెషన్‌లు మరియు చెక్-ఇన్‌లు',        body: 'ఏ తేదీపైనా నొక్కి సెషన్ సృష్టించండి. సెషన్ కార్డ్ నొక్కి బుకింగులు నిర్వహించండి, సభ్యులను చెక్-ఇన్ చేయండి (ప్రారంభానికి 15 నిమిషాల ముందు తెరుచుకుంటుంది) లేదా సెషన్ పూర్తి చేయండి.' },
    { title: 'టెంప్లేట్ సృష్టించండి',             body: 'టెంప్లేట్‌లో బుకబుల్, శాఖ, ట్రైనర్ మరియు గది సేవ్ అవుతాయి — కానీ సమయం కాదు. కోర్టు మరియు స్పా స్లాట్లను ఇలాగే ముందుగా జనరేట్ చేయాలి: ఇప్పటికే ఉన్న స్లాట్లను మాత్రమే సభ్యులు బుక్ చేయగలరు.' },
    { title: 'ఒకేసారి చాలా సెషన్‌లు సృష్టించండి', body: 'వారం రోజులు, సమయం, ప్రారంభ తేదీ మరియు వారాల సంఖ్య ఎంచుకోండి. ఇప్పటికే ఉన్న సెషన్‌లు స్వయంచాలకంగా వదిలివేయబడతాయి — నకళ్ళు ఉండవు. ఉదయం 6 నుండి రాత్రి 10 వరకు కోర్టు స్లాట్లకు సరైనది.' },
    { title: 'వనరులు జోడించండి',                  body: 'ప్రతి శాఖలో స్థలాలు సృష్టించండి — గదులు, స్టూడియోలు, కోర్టులు, థెరపీ గదులు. కోర్టు బుకింగ్‌కు కోర్టు తప్పనిసరి; స్పాకు థెరపీ గది మంచిది. కేటాయించడం వల్ల డబుల్-బుకింగ్ నివారించబడుతుంది.' },
    { title: 'సేవల ద్వారా చెల్లింపు స్వీకరించండి', body: 'Services విభాగంలో ధరతో సేవను సృష్టించండి (ఉదా. Drop-in ₹500, కోర్టు గంట ₹300). షెడ్యూల్ చేసేటప్పుడు లింక్ చేయండి — ప్లాన్ లేదా పాక్ లేని సభ్యుల నుండి బుకింగ్‌పై స్వయంచాలకంగా శుల్కం వసూలు అవుతుంది.' },
  ],
};

const LANG_LABELS: Record<TourLang, string> = { en: 'EN', hi: 'हि', te: 'తె' };

function TourOverlay({ step, onNext, onPrev, onClose }: {
  step: number; onNext: () => void; onPrev: () => void; onClose: () => void;
}) {
  const total = TOUR_STEPS.length;
  const current = TOUR_STEPS[step];
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [lang, setLang] = useState<TourLang>('en');

  const text = TOUR_TEXT[lang][step];

  useEffect(() => {
    setRect(null);
    if (!current.target) return;
    const find = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${current.target}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setTimeout(() => setRect(el.getBoundingClientRect()), 180);
      }
    };
    const t = setTimeout(find, 120);
    return () => clearTimeout(t);
  }, [step, current.target]);

  // Tooltip positioning relative to spotlight rect
  const tooltipStyle = (): React.CSSProperties => {
    const W = 296;
    const GAP = 14;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (!rect || current.placement === 'center') {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: W, zIndex: 10002 };
    }
    const clampL = (v: number) => Math.max(8, Math.min(v, vw - W - 8));
    switch (current.placement) {
      case 'bottom':
        return { position: 'fixed', top: Math.min(rect.bottom + GAP, vh - 200), left: clampL(rect.left + rect.width / 2 - W / 2), width: W, zIndex: 10002 };
      case 'top':
        return { position: 'fixed', bottom: vh - rect.top + GAP, left: clampL(rect.left + rect.width / 2 - W / 2), width: W, zIndex: 10002 };
      case 'left': {
        const left = rect.left - W - GAP;
        if (left < 8) return { position: 'fixed', top: Math.min(rect.bottom + GAP, vh - 200), left: clampL(rect.left + rect.width / 2 - W / 2), width: W, zIndex: 10002 };
        return { position: 'fixed', top: Math.max(8, rect.top + rect.height / 2 - 90), left, width: W, zIndex: 10002 };
      }
      case 'right':
        return { position: 'fixed', top: Math.max(8, rect.top + rect.height / 2 - 90), left: clampL(rect.right + GAP), width: W, zIndex: 10002 };
      default:
        return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: W, zIndex: 10002 };
    }
  };

  const PAD = 8;
  const spotStyle: React.CSSProperties | null = rect ? {
    position: 'fixed',
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
    borderRadius: 12,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.58)',
    border: '2px solid #AAD816',
    outline: '4px solid rgba(170,216,22,0.25)',
    zIndex: 10001,
    pointerEvents: 'none',
  } : null;

  return (
    <>
      {/* Backdrop (only when no rect — rect's box-shadow covers it) */}
      {!rect && <div className="fixed inset-0 bg-black/55 z-[10000]" onClick={onClose} />}
      {/* Spotlight ring */}
      {spotStyle && <div style={spotStyle} />}
      {/* Tooltip card */}
      <div style={tooltipStyle()} className="bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-4">
          {/* Language toggle + close */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-0.5">
              {(Object.keys(LANG_LABELS) as TourLang[]).map(l => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-2.5 py-1 text-[11px] font-black rounded-md transition-colors ${lang === l ? 'bg-[#AAD816] text-zinc-900' : 'text-zinc-400 hover:text-white'}`}
                >
                  {LANG_LABELS[l]}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{step + 1} / {total}</span>
              <div className="flex gap-1">
                {Array.from({ length: total }).map((_, i) => (
                  <div key={i} className={`h-1 rounded-full transition-all duration-300 ${i === step ? 'w-4 bg-[#AAD816]' : i < step ? 'w-1.5 bg-zinc-600' : 'w-1.5 bg-zinc-800'}`} />
                ))}
              </div>
              <button onClick={onClose} className="p-1 text-zinc-600 hover:text-zinc-300 transition-colors ml-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="shrink-0 w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center mt-0.5">
              {current.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-white leading-snug mb-1.5">{text.title}</p>
              <p className="text-xs text-zinc-300 leading-relaxed">{text.body}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 pb-3 pt-1">
          <button onClick={onPrev} disabled={step === 0} className="text-xs font-bold text-zinc-500 hover:text-white disabled:opacity-0 transition-colors px-1 py-1">← Back</button>
          <button onClick={step === total - 1 ? onClose : onNext} className="px-4 py-1.5 text-xs font-bold bg-[#AAD816] text-zinc-900 rounded-lg hover:bg-lime-300 transition-colors">
            {step === total - 1 ? 'Done ✓' : 'Next →'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── SchedulePage ──────────────────────────────────────────────────────────────

export default function SchedulePage() {
  const { userContext } = useUserContext();
  const gymId = userContext?.accessibleGyms?.[0] || '';

  const [view, setView] = useState<'today' | 'bookables' | 'calendar' | 'recurring' | 'resources'>('today');
  const [infoOpen, setInfoOpen] = useState(false);
  const [anchor, setAnchor] = useState(() => new Date());
  const [sessions, setSessions] = useState<ClassSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState<GymClass[]>([]);
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [branchFilter, setBranchFilter] = useState('');

  // One-time banner introducing the bookable types (B11)
  const [showIntroBanner, setShowIntroBanner] = useState(() => localStorage.getItem('bookables-intro-dismissed') !== 'true');
  const dismissIntroBanner = () => {
    localStorage.setItem('bookables-intro-dismissed', 'true');
    setShowIntroBanner(false);
  };

  // Calendar slide panels
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [preDate, setPreDate] = useState<string | undefined>();
  const [preTime, setPreTime] = useState<string | undefined>();
  /* Calendar view state lives on the user, not the browser: an admin who works
     in List on their laptop should land in List on the floor tablet too. The
     hook seeds from a local cache so the first paint is already right, then
     reconciles with the server. */
  const [calPrefs, setCalPrefs] = useUserPreference('bookings_calendar', {
    view: 'pulse' as 'pulse' | 'list',
    heatMetric: 'fill' as HeatMetric,
    density: 'Default' as Density,
  });
  const [timeline, setTimeline] = useState<TimelineInit | null>(null);
  /** Types shown in the calendar. At least one is always on. */
  const [calTypes, setCalTypes] = useState<CalBookableType[]>([...CAL_TYPE_ORDER]);
  const [sessionTimezone, setSessionTimezone] = useState<string | undefined>(undefined);
  /** Re-derives past/live without a refetch. */
  const [nowTick, setNowTick] = useState(() => Date.now());
  const openCreate = (date?: string, time?: string) => {
    setPreDate(date);
    setPreTime(time || undefined);
    setShowCreatePanel(true);
  };
  const [detailSession, setDetailSession] = useState<ClassSession | null>(null);

  // Booking V2 quick-book dialog (optionally prefilled from the day sheet)
  const [showQuickBook, setShowQuickBook] = useState(false);
  const [quickBookPrefill, setQuickBookPrefill] = useState<QuickBookPrefill | undefined>(undefined);

  // Day-sheet refresh: bumped whenever a booking/session change happens elsewhere
  const [daySheetRefresh, setDaySheetRefresh] = useState(0);
  const bumpDaySheet = () => setDaySheetRefresh(r => r + 1);

  // Classes tab
  const [classSearch, setClassSearch] = useState('');
  const [showInactiveClasses, setShowInactiveClasses] = useState(false);
  const [showClassFormPanel, setShowClassFormPanel] = useState(false);
  const [editingClass, setEditingClass] = useState<Partial<GymClass> | null>(null);
  const [classRefresh, setClassRefresh] = useState(0);

  // Recurring / Rooms external create triggers
  const [triggerRecurringCreate, setTriggerRecurringCreate] = useState(false);
  const [triggerRoomsCreate, setTriggerRoomsCreate] = useState(false);

  // Learning tour
  const [tourStep, setTourStep] = useState<number | null>(null);

  const startTour = () => { setTourStep(0); setView('bookables'); };
  const tourNext = () => {
    const next = (tourStep ?? 0) + 1;
    if (next >= TOUR_STEPS.length) { setTourStep(null); return; }
    setTourStep(next);
    setView(TOUR_STEPS[next].tab);
  };
  const tourPrev = () => {
    const prev = (tourStep ?? 0) - 1;
    if (prev < 0) return;
    setTourStep(prev);
    setView(TOUR_STEPS[prev].tab);
  };

  const weekDates = useMemo(() => getWeekDates(anchor), [anchor]);

  /** Anything in flight — first loads, week changes and background refreshes.
   *  Every calendar surface shows a spinner off this, so a view is never
   *  silently stale while it refetches. */
  const [calBusy, setCalBusy] = useState(0);

  const fetchSessions = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setCalBusy(c => c + 1);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const dateFrom = toDateStr(weekDates[0]);
      const dateTo = toDateStr(weekDates[6]);

      const res = await fetch(
        `${API_BASE_URL}/api/class-sessions?dateFrom=${dateFrom}&dateTo=${dateTo}&withAttendee=1`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'x-client-type': 'web',
          },
        }
      );
      const data = await res.json();
      if (data.success) setSessions(data.data);
      if (data.timezone) setSessionTimezone(data.timezone as string);
    } catch {
      toast.error('Failed to load sessions');
    } finally {
      setCalBusy(c => c - 1);
      if (showSpinner) setLoading(false);
    }
  };

  const fetchSupportingData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const headers = {
        Authorization: `Bearer ${session.access_token}`,
        'x-client-type': 'web',
      };

      const [classesRes, trainersRes, branchesRes, servicesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/classes`, { headers }),
        fetch(`${API_BASE_URL}/api/trainer-availability/trainers`, { headers }),
        fetch(`${API_BASE_URL}/api/branches`, { headers }),
        fetch(`${API_BASE_URL}/api/services?includeInactive=false`, { headers }),
      ]);

      const [classesData, trainersData, branchesData, servicesData] = await Promise.all([
        classesRes.json(), trainersRes.json(), branchesRes.json(), servicesRes.json(),
      ]);

      if (classesData.success) setClasses(classesData.data);
      if (trainersData.success) setTrainers(trainersData.data);
      if (branchesData.success) {
        const mappedBranches = branchesData.data.map((b: any) => ({
          ...b,
          gymId: b.gym?.id || gymId,
        }));
        setBranches(mappedBranches);
        const firstGymId = mappedBranches[0]?.gymId || gymId;
        if (firstGymId) {
          const roomsRes = await fetch(`${API_BASE_URL}/api/classes/${firstGymId}/rooms`, { headers });
          const roomsData = await roomsRes.json();
          if (roomsData.success) setRooms(roomsData.data);
        }
      }
      if (servicesData.success) {
        setServices(servicesData.data.filter((s: Service) =>
          s.serviceType === 'CLASS_SESSION' || s.serviceType === 'STANDARD'
        ));
      }
    } catch {}
  };

  useEffect(() => { fetchSupportingData(); }, [gymId]);
  useEffect(() => { if (weekDates.length) fetchSessions(); }, [weekDates]);

  // ── Calendar model ─────────────────────────────────────────────────────────
  // The week's rows, flattened once into the shape every calendar surface reads.

  // Keeps the now marker and the past/live styling honest without refetching.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calNow = useMemo(() => tzNow(sessionTimezone), [sessionTimezone, nowTick]);

  const weekDateStrs = useMemo(() => weekDates.map(toDateStr), [weekDates]);

  const calDays = useMemo<PulseDay[]>(
    () =>
      weekDates.map((d, i) => ({
        i,
        dow: DAY_NAMES[i],
        date: d.getDate(),
        mon: d.toLocaleDateString('en-IN', { month: 'short' }),
        dateStr: weekDateStrs[i],
        today: weekDateStrs[i] === calNow.dateStr,
      })),
    [weekDates, weekDateStrs, calNow.dateStr]
  );

  const weekLabel = useMemo(() => {
    const a = weekDates[0];
    const z = weekDates[6];
    if (!a || !z) return '';
    const sameMonth = a.getMonth() === z.getMonth();
    const zLabel = z.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    return sameMonth
      ? `${a.getDate()} – ${zLabel}`
      : `${a.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${zLabel}`;
  }, [weekDates]);

  /** Every session of the week in calendar shape, before the type filter — the
   *  legend counts have to keep showing what you would get back by re-enabling
   *  a type. */
  const calAll = useMemo(() => {
    const namer = staffNamer(trainers);
    return sessions
      .filter(s => !branchFilter || s.branchId === branchFilter)
      .map(s => toCalSession(s as unknown as ApiSession, { staffName: namer, now: calNow, weekDates: weekDateStrs }))
      .filter(s => s.day >= 0);
  }, [sessions, branchFilter, trainers, calNow, weekDateStrs]);

  const calCounts = useMemo(() => {
    const c: Partial<Record<CalBookableType, number>> = {};
    for (const s of calAll) c[s.type] = (c[s.type] || 0) + 1;
    return c;
  }, [calAll]);

  const calSessions = useMemo(() => calAll.filter(s => calTypes.includes(s.type)), [calAll, calTypes]);

  const calHours = useMemo(() => hourWindow(calSessions), [calSessions]);

  /** Header stats. Cancelled sessions are excluded — they are not load. */
  const calStats = useMemo(() => {
    const live = calSessions.filter(s => s.status !== 'cancelled');
    const booked = live.reduce((a, s) => a + s.booked, 0);
    const cap = live.reduce((a, s) => a + s.cap, 0);
    return {
      sessions: live.length,
      booked,
      fill: cap ? Math.round((booked / cap) * 100) : 0,
      full: live.filter(s => s.cap > 0 && s.booked >= s.cap).length,
      wait: live.reduce((a, s) => a + s.wait, 0),
    };
  }, [calSessions]);

  const toggleCalType = (t: CalBookableType) =>
    setCalTypes(prev => (prev.includes(t) ? (prev.length > 1 ? prev.filter(x => x !== t) : prev) : [...prev, t]));

  /** Back to the ClassSession the slide panels expect. */
  const openCalSession = (s: CalSession) => setDetailSession(s.raw as ClassSession);

  const calFilters = (
    <>
      <BranchSelect value={branchFilter} onChange={setBranchFilter} branches={branches} />
      <TypeFilter types={calTypes} onChange={setCalTypes} counts={calCounts} />
    </>
  );

  const openClassForm = (gymClass: Partial<GymClass> | 'new') => {
    setEditingClass(gymClass === 'new' ? { gymId } : gymClass);
    setShowClassFormPanel(true);
  };

  // Action button per tab
  const renderActionButton = () => {
    switch (view) {
      case 'bookables':
        return (
          <button
            data-tour="tour-new-bookable"
            onClick={() => openClassForm('new')}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-sm font-semibold rounded-xl hover:bg-zinc-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            New Bookable
          </button>
        );
      case 'calendar':
        return (
          <button
            data-tour="tour-new-session"
            onClick={() => openCreate()}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-sm font-semibold rounded-xl hover:bg-zinc-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Schedule
          </button>
        );
      case 'recurring':
        return (
          <button
            data-tour="tour-new-template"
            onClick={() => setTriggerRecurringCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-sm font-semibold rounded-xl hover:bg-zinc-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            New Template
          </button>
        );
      case 'resources':
        return (
          <button
            data-tour="tour-add-resource"
            onClick={() => setTriggerRoomsCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-sm font-semibold rounded-xl hover:bg-zinc-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Resource
          </button>
        );
    }
  };

  // Sub-bar per tab
  const renderSubBar = () => {
    if (view === 'bookables') {
      return (
        <div className="border-b bg-gray-50 px-6 py-2.5 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={classSearch}
              onChange={e => setClassSearch(e.target.value)}
              placeholder="Search bookables…"
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
            />
          </div>
          <button
            onClick={() => setShowInactiveClasses(v => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
              showInactiveClasses ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {showInactiveClasses ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            Show Inactive
          </button>
        </div>
      );
    }
    if (view === 'calendar') {
      return (
        <div className="acal border-b bg-gray-50 px-4 sm:px-6 py-2.5">
          <div className="a-bar">
            {calFilters}
            <WeekNav
              label={weekLabel}
              onPrev={() => setAnchor(a => { const d = new Date(a); d.setDate(d.getDate() - 7); return d; })}
              onNext={() => setAnchor(a => { const d = new Date(a); d.setDate(d.getDate() + 7); return d; })}
            />
            <button className="a-pill" onClick={() => setAnchor(new Date())}>Today</button>
            <Segmented
              value={calPrefs.view}
              onChange={v => setCalPrefs({ view: v })}
              options={[['pulse', 'Week pulse'], ['list', 'List']]}
              ariaLabel="Calendar view"
            />
            {calBusy > 0 && <BusyPill />}
            <div className="a-sp" />
            <button className="a-btn dark" onClick={() => setTimeline({ mode: 'week' })}>
              Open full timeline <Maximize2 className="w-3.5 h-3.5 opacity-70" />
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-gray-100 bg-white">
        {/* Row 1: title + action */}
        <div className="flex items-center justify-between px-4 sm:px-6 pt-4 pb-2 sm:pb-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-black text-gray-900">Bookings</h1>
            <button
              onClick={startTour}
              title="Take a guided tour"
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <button
              onClick={() => { setQuickBookPrefill(undefined); setShowQuickBook(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <Zap className="w-4 h-4" />
              Quick book
            </button>
            {renderActionButton()}
          </div>
        </div>

        {/* One-time intro banner: classes → bookables (B11) */}
        {showIntroBanner && (
          <div className="mx-4 sm:mx-6 mt-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <span className="text-lg leading-none mt-0.5">✨</span>
            <div className="flex-1 text-xs text-emerald-800 leading-relaxed">
              <span className="font-bold">Classes is now Bookings.</span>{' '}
              Alongside classes you can now offer <strong>Spa &amp; recovery</strong>, <strong>Personal training</strong>,{' '}
              <strong>Consults</strong> and <strong>Facilities</strong> (courts, steam rooms, pools…) — pick the type when you create a bookable.
              Members can only book slots you've scheduled in advance, so for all-day facility or spa availability,
              generate the slots ahead of time with a <strong>Recurring template</strong>.
            </div>
            <button onClick={dismissIntroBanner} className="p-1 text-emerald-500 hover:text-emerald-700 transition-colors shrink-0" title="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* How members pay — collapsible info strip */}
        <div className="mx-4 sm:mx-6 mt-2 mb-1 bg-blue-50 border border-blue-100 rounded-xl overflow-hidden">
          <button
            onClick={() => setInfoOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-blue-100/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs font-bold text-blue-700">How do members pay for bookings?</span>
            </div>
            <svg className={`w-3.5 h-3.5 text-blue-400 transition-transform duration-200 ${infoOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {infoOpen && (
            <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-3 gap-2 border-t border-blue-100">
              {[
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c0 1.306.835 2.417 2 2.83V19h2v-1.17A3.001 3.001 0 0013 14H9z" />,
                  label: 'Membership Plan',
                  desc: 'Classes included in a plan. Members book for free up to their monthly limit.',
                },
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />,
                  label: 'Credit Pack',
                  desc: 'Sell a bundle of class credits (e.g. "10 Yoga sessions"). One credit is used per booking.',
                },
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />,
                  label: 'Pay Per Session',
                  desc: 'Member pays each time they book. Link a Service with a price to the session to enable this.',
                },
              ].map(({ icon, label, desc }) => (
                <div key={label} className="flex items-start gap-3 bg-white/70 border border-blue-100 rounded-xl px-3.5 py-3 mt-0">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {icon}
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-blue-800 leading-snug">{label}</p>
                    <p className="text-[11px] text-blue-600 leading-relaxed mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Row 2 (mobile): tabs full-width */}
        <div className="sm:hidden px-4 py-2">
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-xl p-1 w-full">
            {(['today', 'bookables', 'calendar', 'recurring', 'resources'] as const).map(tab => (
              <button key={tab} onClick={() => setView(tab)}
                className={`flex-1 py-1.5 text-xs font-bold rounded-lg capitalize transition-colors ${view === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Row 1 desktop: centered tabs overlaid */}
        <div className="hidden sm:flex items-center justify-center px-6 py-3">
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-xl p-1">
            {(['today', 'bookables', 'calendar', 'recurring', 'resources'] as const).map(tab => (
              <button key={tab} onClick={() => setView(tab)}
                className={`px-4 py-1.5 text-xs font-bold rounded-lg capitalize transition-colors ${view === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sub-bar (tab-specific filters/nav) */}
      {renderSubBar()}

      {/* Tab content */}
      {view === 'today' && (
        <BookingDaySheet
          gymId={gymId}
          branches={branches}
          trainers={trainers}
          rooms={rooms}
          classes={classes}
          types={calTypes}
          onTypesChange={setCalTypes}
          refreshSignal={daySheetRefresh}
          onSessionClick={s => setDetailSession(s)}
          onQuickBook={p => { setQuickBookPrefill(p); setShowQuickBook(true); }}
        />
      )}

      {view === 'bookables' && (
        <div className="flex-1 overflow-auto">
          <ClassesPanel
            gymId={gymId}
            search={classSearch}
            showInactive={showInactiveClasses}
            onShowClassForm={openClassForm}
            refreshSignal={classRefresh}
          />
        </div>
      )}

      {view === 'resources' && (
        <RoomsPanel
          gymId={gymId}
          branches={branches}
          onRoomsChange={setRooms}
          externalShowCreate={triggerRoomsCreate}
          onExternalShowCreate={setTriggerRoomsCreate}
        />
      )}

      {view === 'recurring' && (
        <RecurringTemplatesPanel
          gymId={gymId}
          classes={classes}
          trainers={trainers}
          branches={branches}
          services={services}
          rooms={rooms}
          externalShowCreate={triggerRecurringCreate}
          onExternalShowCreate={setTriggerRecurringCreate}
        />
      )}

      {view === 'calendar' && (
        <div className="acal flex-1 overflow-auto p-4 sm:p-6" style={{ background: '#F5F6F8' }}>
          <div className="a-card" style={{ position: 'relative' }}>
            <div className="a-ch">
              <h2>{calPrefs.view === 'pulse' ? 'Week pulse' : 'All sessions'}</h2>
              <div className="a-stats">
                <div><b>{calStats.sessions}</b><span>sessions</span></div>
                <div><b>{calStats.booked.toLocaleString('en-IN')}</b><span>bookings</span></div>
                <div><b>{calStats.fill}%</b><span>avg fill</span></div>
                <div><b>{calStats.full}</b><span>full</span></div>
                <div><b>{calStats.wait}</b><span>waitlisted</span></div>
              </div>
              <div className="a-sp" />
              {calPrefs.view === 'pulse' && (
                <div className="a-heatby">
                  Heat by
                  <Segmented
                    value={calPrefs.heatMetric}
                    onChange={m => setCalPrefs({ heatMetric: m })}
                    options={[['fill', 'Fill %'], ['bookings', 'Bookings'], ['sessions', 'Sessions']]}
                    small
                    ariaLabel="Heat metric"
                  />
                </div>
              )}
            </div>

            {loading ? (
              <div className="a-empty" style={{ padding: '80px 20px' }}>
                <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
              </div>
            ) : calPrefs.view === 'pulse' ? (
              <WeekPulse
                sessions={calSessions}
                days={calDays}
                metric={calPrefs.heatMetric}
                hours={calHours}
                now={calNow}
                onOpen={setTimeline}
              />
            ) : (
              <CalendarList sessions={calSessions} days={calDays} onPick={openCalSession} />
            )}

            <div className="a-legend">
              <TypeLegend types={calTypes} onToggle={toggleCalType} counts={calCounts} />
              {calPrefs.view === 'pulse' && (
                <div className="a-lkey">
                  <span className="pk"><i style={{ background: '#121417' }} />full</span>
                  <span className="pk"><i />open</span>
                  <span className="ramp">
                    Quiet {HEAT_RAMP.slice(1).map(c => <i key={c} style={{ background: c }} />)} Busy
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {timeline && (
        <FullTimelineModal
          /* Keyed on what was clicked, not on the week: navigating weeks inside
             the modal must keep the mode, the day and the scroll position. */
          key={`${timeline.mode}-${timeline.day ?? ''}-${timeline.hour ?? ''}`}
          sessions={calSessions}
          days={calDays}
          init={timeline}
          weekLabel={weekLabel}
          hours={calHours}
          now={calNow}
          filters={calFilters}
          loading={calBusy > 0}
          density={calPrefs.density}
          onDensity={d => setCalPrefs({ density: d })}
          onClose={() => setTimeline(null)}
          onPrevWeek={() => setAnchor(a => { const d = new Date(a); d.setDate(d.getDate() - 7); return d; })}
          onNextWeek={() => setAnchor(a => { const d = new Date(a); d.setDate(d.getDate() + 7); return d; })}
          onOpenSession={s => { setTimeline(null); openCalSession(s); }}
          onEditSession={s => { setTimeline(null); openCalSession(s); }}
        />
      )}

      {/* Slide panels */}

      {/* Create session panel */}
      <SlidePanel open={showCreatePanel} onClose={() => setShowCreatePanel(false)} title="Schedule Session">
        {showCreatePanel && (
          <CreateSessionForm
            key={`${preDate || ''}-${preTime || ''}`}
            gymId={gymId}
            preDate={preDate}
            preTime={preTime}
            classes={classes}
            trainers={trainers}
            branches={branches}
            rooms={rooms}
            services={services}
            onClose={() => setShowCreatePanel(false)}
            onSaved={() => { fetchSessions(false); bumpDaySheet(); }}
          />
        )}
      </SlidePanel>

      {/* Session detail panel */}
      <SlidePanel
        open={!!detailSession}
        onClose={() => setDetailSession(null)}
        title={detailSession ? detailSession.class.name : ''}
      >
        {detailSession && (
          <SessionDetailPanel
            session={detailSession}
            onClose={() => setDetailSession(null)}
            onUpdated={() => { fetchSessions(false); bumpDaySheet(); }}
            trainers={trainers}
            rooms={rooms}
          />
        )}
      </SlidePanel>

      {/* Class form panel */}
      <SlidePanel
        open={showClassFormPanel}
        onClose={() => { setShowClassFormPanel(false); setEditingClass(null); }}
        title={editingClass?.id ? 'Edit Bookable' : 'New Bookable'}
      >
        {showClassFormPanel && (
          <ClassForm
            gymClass={editingClass}
            gymId={gymId}
            onClose={() => { setShowClassFormPanel(false); setEditingClass(null); }}
            onSaved={() => { fetchSupportingData(); setClassRefresh(r => r + 1); }}
          />
        )}
      </SlidePanel>

      {/* Quick-book dialog (Booking V2) */}
      {showQuickBook && (
        <QuickBookModal
          gymId={gymId}
          classes={classes}
          branches={branches}
          initial={quickBookPrefill}
          onClose={() => { setShowQuickBook(false); setQuickBookPrefill(undefined); }}
          onBooked={() => { fetchSessions(false); bumpDaySheet(); }}
          onCreateBookable={() => { setShowQuickBook(false); setQuickBookPrefill(undefined); openClassForm('new'); }}
        />
      )}

      {/* Learning tour overlay */}
      {tourStep !== null && (
        <TourOverlay
          step={tourStep}
          onNext={tourNext}
          onPrev={tourPrev}
          onClose={() => setTourStep(null)}
        />
      )}
    </div>
  );
}
